import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import KitSelectionStep from "../components/KitSelectionStep";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, Check, AlertCircle, DollarSign, Gift, TrendingDown, Users, Save, Clock, Tag, CheckCircle, RefreshCw, Calendar, Plus, Trophy, TrendingUp, Smile, Zap, Sparkles, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { ContentWithImages } from "@/components/ImageTextarea";

// Ensure cookies are sent with all requests
axios.defaults.withCredentials = true;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Programme Type presentation presets — matches admin dropdown in AcademyTermManagement.js
// Keep in sync with the enum stored on academy_terms.programme_type
const PROGRAMME_TYPES = {
  competitive: {
    label: "Competitive",
    Icon: Trophy,
    pill: "bg-amber-500/15 text-amber-300 border border-amber-400/30",
    accent: "border-amber-500/40",
    defaultBlurb: "All-access training. Players are up for game-day selection.",
  },
  development: {
    label: "Development",
    Icon: TrendingUp,
    pill: "bg-cyan-500/15 text-cyan-300 border border-cyan-400/30",
    accent: "border-cyan-500/40",
    defaultBlurb: "Skill-focused training. No competitive fixtures.",
  },
  recreational: {
    label: "Recreational",
    Icon: Smile,
    pill: "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30",
    accent: "border-emerald-500/40",
    defaultBlurb: "Social, low-pressure sessions. Play and enjoy.",
  },
  standard: {
    label: "Standard Term",
    Icon: Calendar,
    pill: "bg-slate-500/15 text-slate-300 border border-slate-400/30",
    accent: "border-slate-500/40",
    defaultBlurb: "Regular full-term training programme.",
  },
  adhoc: {
    label: "Ad-Hoc",
    Icon: Zap,
    pill: "bg-blue-500/15 text-blue-300 border border-blue-400/30",
    accent: "border-blue-500/40",
    defaultBlurb: "Pay per session. Flexible, no full-term commitment.",
  },
  girls: {
    label: "Girls",
    Icon: Users,
    pill: "bg-rose-500/15 text-rose-300 border border-rose-400/30",
    accent: "border-rose-500/40",
    defaultBlurb: "Girls-only training with dedicated pricing.",
  },
};

// Filter tabs shown to parents on Step 2 (order matters)
const PROGRAMME_FILTER_TABS = [
  { key: "all", label: "See All" },
  { key: "competitive", label: "Competitive" },
  { key: "development", label: "Development" },
  { key: "recreational", label: "Recreational" },
];

// Initial child state - simplified: only essential fields upfront
// Non-essential fields (ID doc, nationality, school, email, emergency) collected post-registration
const createInitialChild = (parentName = "", parentPhone = "") => ({
  given_name: "",
  last_name: "",
  birthdate: "",
  id_document_number: "",
  gender: "male",
  nationality: "",
  school: "",
  email: "",
  medical_conditions: "",
  emergency_contact_name: parentName,
  emergency_contact: parentPhone,
  emergency_relationship: "Parent",
  medical_info: "",
  // Parent-controlled: checked by default, unchecked if they've already paid
  // the one-time registration fee (e.g. legacy academy player pre-portal).
  // Admin verifies manually at payment approval.
  charge_registration_fee: true,
});

// Fallback for cases where parent info isn't available yet
const INITIAL_CHILD = createInitialChild();

export default function ParentRegistrationWizard({ user: propUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Draft registration state
  const [draftId, setDraftId] = useState(searchParams.get('draft') || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);

  // Soft duplicate-account suggestions (phone / name / child match)
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [duplicateDismissed, setDuplicateDismissed] = useState(false);
  
  // Determine if propUser is valid (has id or _id)
  const isValidPropUser = propUser && (propUser.id || propUser._id);
  
  // Check if user is already logged in - use prop first, then fallback to API check
  const [loggedInUser, setLoggedInUser] = useState(isValidPropUser ? propUser : null);
  // Existing children already registered under this parent (fetched on login)
  // Used for (1) sibling-discount count across current + prior enrolments,
  // (2) skipping re-charge of registration fee for kids who already paid it.
  const [existingChildren, setExistingChildren] = useState([]);
  const [isExistingMember, setIsExistingMember] = useState(!!isValidPropUser);
  const [checkingAuth, setCheckingAuth] = useState(!isValidPropUser); // Skip check if user prop provided
  
  // If user prop is provided, start at step 1 (sessions) since parent info is pre-filled
  // NEW: 3-step flow: Step 1 = Parent+Children, Step 2 = Sessions, Step 3 = Review+Pay
  const [step, setStep] = useState(isValidPropUser ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailAction, setEmailAction] = useState(null); // "login", "login_and_pay"
  const [pendingPaymentInfo, setPendingPaymentInfo] = useState(null);
  
  // Membership validation
  const [membershipNumber, setMembershipNumber] = useState("");
  const [membershipValidating, setMembershipValidating] = useState(false);
  const [membershipValid, setMembershipValid] = useState(null); // null=not checked, true=valid, false=invalid
  // For family-suffix memberships (HFM/WFM/FFM/MFM) we collect the primary SCC
  // member's full name + relationship so admin can verify manually at payment time.
  const [primaryMemberGivenName, setPrimaryMemberGivenName] = useState("");
  const [primaryMemberLastName, setPrimaryMemberLastName] = useState("");
  const [relationshipToPrimary, setRelationshipToPrimary] = useState("");

  // Step 1: Parent Info - pre-fill from prop if available
  const [parentGivenName, setParentGivenName] = useState(propUser?.given_name || propUser?.name?.split(' ')[0] || "");
  const [parentLastName, setParentLastName] = useState(propUser?.last_name || propUser?.name?.split(' ').slice(1).join(' ') || "");
  const [parentEmail, setParentEmail] = useState(propUser?.email || "");
  const [parentPhone, setParentPhone] = useState(propUser?.phone || "");
  const [parentPassword, setParentPassword] = useState("");
  const [parentAddress, setParentAddress] = useState(propUser?.address || "");
  const [parentNationality, setParentNationality] = useState(propUser?.nationality || "");
  const [showPassword, setShowPassword] = useState(false);
  // Member status - should be based on explicit is_scc_member flag, NOT just having membership_number
  const [isSccMember, setIsSccMember] = useState(propUser?.is_scc_member || propUser?.is_section_member || false);

  // Step 2: Children Info - pre-fill emergency contact with parent info
  const getParentFullName = () => {
    if (propUser) {
      return `${propUser.given_name || ''} ${propUser.last_name || propUser.name || ''}`.trim();
    }
    return '';
  };
  
  const [children, setChildren] = useState([
    createInitialChild(
      getParentFullName(),
      propUser?.phone || ""
    )
  ]);

  // Step 2: Terms & Sessions (was Step 3)
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [sessions, setSessions] = useState([]);
  // Per-child session selections: { childIndex: [sessionIds] }
  const [childSessionSelections, setChildSessionSelections] = useState({});

  // Resume-existing-pending-registration modal (Feb 2026 duplicate-guard).
  // Populated by an API check when a term is selected — we only show it when
  // the parent is signed in AND already has an in-progress registration for
  // this term. Null while loading / no match.
  const [pendingResumeReg, setPendingResumeReg] = useState(null);
  const [resumeCheckDismissed, setResumeCheckDismissed] = useState(false);
  const [supersedingPending, setSupersedingPending] = useState(false);
  // Duplicate-child warn-and-confirm (Jun 2026). When register-parent returns
  // a 409 duplicate_child, we surface a modal instead of silently creating a
  // second record. Holds the array of matched duplicates from the API.
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [existingAccount, setExistingAccount] = useState(null);
  
  // Session type filter (Social/Competitive/All)
  const [sessionTypeFilter, setSessionTypeFilter] = useState("all"); // all, social, competitive

  // Programme Type filter on Step 2 term picker (all/competitive/development/recreational)
  const [programmeFilter, setProgrammeFilter] = useState("all");

  // Step 4: Fee Calculation & Upsell
  const [feeCalculation, setFeeCalculation] = useState(null);
  // Kit chosen at registration (free bundle sizes + paid extras)
  const [kitSelections, setKitSelections] = useState({});
  const [kitExtrasTotal, setKitExtrasTotal] = useState(0);
  const kitChildrenList = useMemo(() => {
    const nameToId = {};
    (existingChildren || []).forEach((ec) => {
      const nm = (ec.name || `${ec.given_name || ""} ${ec.last_name || ""}`).trim().toLowerCase();
      if (nm) nameToId[nm] = ec.id || ec.player_id || null;
    });
    // The free kit comes with the one-time registration fee, so a child who is
    // not being charged it (already registered in an earlier term) is not
    // offered a free kit - only paid extras.
    const notCharged = {};
    (children || []).forEach((c) => {
      const nm = `${c.given_name || ""} ${c.last_name || ""}`.trim().toLowerCase() || (c.name || "").toLowerCase();
      if (nm) notCharged[nm] = c.charge_registration_fee === false;
    });
    return (feeCalculation?.child_breakdowns || [])
      .map((cb) => ({
        key: cb.name,
        name: cb.name,
        playerId: nameToId[(cb.name || "").trim().toLowerCase()] || null,
        paidRegistrationFee: !notCharged[(cb.name || "").trim().toLowerCase()],
      }))
      .filter((c) => c.name);
  }, [
    JSON.stringify((feeCalculation?.child_breakdowns || []).map((c) => c.name)),
    JSON.stringify((existingChildren || []).map((e) => ({ i: e.id || e.player_id, n: e.name }))),
    JSON.stringify((children || []).map((c) => ({
      n: `${c.given_name || ""} ${c.last_name || ""}`.trim(), f: c.charge_registration_fee }))),
  ]);
  const [rateTable, setRateTable] = useState(null);
  const [includeDM, setIncludeDM] = useState(false);
  const [skipRegistrationFee, setSkipRegistrationFee] = useState(false); // Parent can opt-out of registration fee
  const [selectedOptionalItems, setSelectedOptionalItems] = useState([]);
  const [optionalItems, setOptionalItems] = useState([]);

  // Discount Code
  const [discountCode, setDiscountCode] = useState("");
  const [validatedDiscount, setValidatedDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState("");

  // Payment Method (part of Step 3 now)
  const [paymentMethod, setPaymentMethod] = useState("paynow"); // default to paynow (no fees)
  const CARD_FEE_PERCENT = 3.4;

  // Consent checkboxes
  const [consentPDPA, setConsentPDPA] = useState(false);
  const [consentSafeSport, setConsentSafeSport] = useState(false);
  const [consentIndemnity, setConsentIndemnity] = useState(false);

  // Consent texts from database
  const [consentTexts, setConsentTexts] = useState([]);

  // Check if all consents are given
  const activeConsentCount = consentTexts.filter(t => t.is_active).length;
  const givenConsents = [consentPDPA, consentSafeSport, consentIndemnity].filter(Boolean).length;
  const allConsentsGiven = activeConsentCount > 0 ? givenConsents >= activeConsentCount : (consentPDPA && consentSafeSport && consentIndemnity);

  // Fetch consent texts
  useEffect(() => {
    const fetchConsentTexts = async () => {
      try {
        const response = await axios.get(`${API}/academy/consent-texts`);
        setConsentTexts(response.data || []);
      } catch (error) {
        console.error("Error fetching consent texts:", error);
        // Fallback to default if fetch fails
        setConsentTexts([]);
      }
    };
    fetchConsentTexts();
  }, []);

  // Reset form when navigating to this page
  const resetForm = useCallback(() => {
    setStep(1);
    setLoading(false);
    setEmailError("");
    setParentGivenName("");
    setParentLastName("");
    setParentEmail("");
    setParentPhone("");
    setParentPassword("");
    setShowPassword(false);
    setIsSccMember(false);
    setChildren([{ ...INITIAL_CHILD }]);
    setTerms([]);
    setSelectedTerm(null);
    setSessions([]);
    setChildSessionSelections({});
    setFeeCalculation(null);
    setRateTable(null);
    setIncludeDM(false);
    setSkipRegistrationFee(false);
    setSelectedOptionalItems([]);
    setOptionalItems([]);
    setPaymentMethod("paynow");
  }, []);

  // Check if user is already logged in and pre-fill their info
  useEffect(() => {
    // Shared helper: seed `children` state from the parent's linked academy_players so returning
    // parents see their existing kids as pre-ticked cards instead of a blank form.
    const seedExistingChildren = async (user) => {
      try {
        const kidsRes = await axios.get(`${API}/academy/my-children`, { withCredentials: true });
        const kids = Array.isArray(kidsRes.data) ? kidsRes.data : (kidsRes.data?.children || []);
        setExistingChildren(kids);
        if (kids.length > 0) {
          const parentFull = `${user.given_name || ""} ${user.last_name || user.name || ""}`.trim();
          const parentPhoneVal = user.phone || "";
          setChildren(kids.map((k) => ({
            existing_child_id: k.id,
            is_existing_child: true,
            include_this_term: true,
            given_name: k.given_name || (k.name ? k.name.split(" ")[0] : "") || "",
            last_name: k.last_name || (k.name ? k.name.split(" ").slice(1).join(" ") : "") || "",
            birthdate: k.birthdate || k.date_of_birth || "",
            id_document_number: k.id_document_number || "",
            gender: k.gender || "male",
            nationality: k.nationality || "",
            school: k.school || "",
            email: k.email || "",
            medical_conditions: k.medical_info || k.medical_conditions || "",
            medical_info: k.medical_info || k.medical_conditions || "",
            emergency_contact_name: k.emergency_contact_name || parentFull,
            emergency_contact: k.emergency_contact || parentPhoneVal,
            emergency_relationship: k.emergency_relationship || "Parent",
            // The one-time registration fee is waived ONLY when the child has
            // actually PAID for an earlier registration. A player record is
            // created the moment a registration is started, so an abandoned
            // unpaid attempt must not buy a first-timer a free reg fee and kit.
            charge_registration_fee: k.registration_fee_paid !== true,
          })));
        }
      } catch (e) {
        console.warn("Could not fetch existing children:", e?.response?.status);
      }
    };

    // If user prop is provided from App.js we still need to seed the children list
    if (propUser && (propUser.id || propUser._id)) {
      console.log("User prop provided from App.js:", propUser.email);
      seedExistingChildren(propUser);
      setLoggedInUser(propUser);
      setIsExistingMember(true);
      setIsSccMember(propUser.is_scc_member || propUser.is_section_member || false);
      setStep(1); // start on the children step so returning parent sees their pre-ticked kids
      toast.success(`Welcome back, ${propUser.given_name || propUser.name}! Tick the child(ren) to enroll for the next term.`);
      return;
    }

    // Fallback: API check if no user prop (for direct navigation or unauthenticated users)
    const checkLoggedInUser = async () => {
      setCheckingAuth(true);
      try {
        console.log("Checking if user is logged in via API...");
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        console.log("Auth response:", response.status, response.data);
        
        // Check both `id` and `_id` for the response
        if (response.data && (response.data.id || response.data._id)) {
          const user = response.data;
          console.log("Logged in user detected:", user.email, "Roles:", user.roles);
          setLoggedInUser(user);
          setIsExistingMember(true);
          
          // Pre-fill parent info from logged-in user
          setParentGivenName(user.given_name || user.name?.split(' ')[0] || "");
          setParentLastName(user.last_name || user.name?.split(' ').slice(1).join(' ') || "");
          setParentEmail(user.email || "");
          setParentPhone(user.phone || "");
          // Set member status based on explicit flag, not just membership number
          setIsSccMember(user.is_scc_member || user.is_section_member || false);
          
          // Fetch existing children so we can (a) skip re-charging registration fees and
          // (b) include them in the sibling-discount count.
          try {
            const kidsRes = await axios.get(`${API}/academy/my-children`, { withCredentials: true });
            const kids = Array.isArray(kidsRes.data) ? kidsRes.data : (kidsRes.data?.children || []);
            setExistingChildren(kids);

            // Pre-populate the children wizard state from the parent's existing
            // academy_players so returning parents can just tick the kid + pick
            // sessions for the new term — no re-typing DOB/medical, and no duplicate
            // academy_players records get created at the backend.
            if (kids.length > 0) {
              const parentFull = `${user.given_name || ""} ${user.last_name || user.name || ""}`.trim();
              const parentPhoneVal = user.phone || "";
              setChildren(kids.map((k) => ({
                existing_child_id: k.id,
                is_existing_child: true,
                include_this_term: true, // defaults to ticked; parent can untick to skip
                given_name: k.given_name || (k.name ? k.name.split(" ")[0] : "") || "",
                last_name: k.last_name || (k.name ? k.name.split(" ").slice(1).join(" ") : "") || "",
                birthdate: k.birthdate || k.date_of_birth || "",
                id_document_number: k.id_document_number || "",
                gender: k.gender || "male",
                nationality: k.nationality || "",
                school: k.school || "",
                email: k.email || "",
                medical_conditions: k.medical_info || k.medical_conditions || "",
                medical_info: k.medical_info || k.medical_conditions || "",
                emergency_contact_name: k.emergency_contact_name || parentFull,
                emergency_contact: k.emergency_contact || parentPhoneVal,
                emergency_relationship: k.emergency_relationship || "Parent",
                // Waived only on evidence of a PAID earlier registration; a
                // started-but-never-paid attempt leaves the child a first-timer.
                charge_registration_fee: k.registration_fee_paid !== true,
              })));
            }
          } catch (e) {
            console.warn("Could not fetch existing children:", e?.response?.status);
          }

          // Start on Step 1 (Children) so returning parents see their existing kids as
          // pre-ticked cards and can add a new sibling if needed. My earlier fix only
          // reached step 1; setStep(2) skipped it entirely.
          setStep(1);

          toast.success(`Welcome back, ${user.given_name || user.name}! Tick the child(ren) to enroll for the next term.`);
        } else {
          console.log("Auth response but no user id:", response.data);
          setIsExistingMember(false);
        }
      } catch (error) {
        // Not logged in - normal registration flow
        console.log("Auth check failed:", error.response?.status, error.message);
        setIsExistingMember(false);
        setLoggedInUser(null);
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkLoggedInUser();
  }, []); // Only run once on mount

  // Check for linked registration data from Section Member / Non-Football Section flows
  const [hasLinkedRegistration, setHasLinkedRegistration] = useState(false);
  const [linkedMemberType, setLinkedMemberType] = useState(null); // 'section' or 'scc_member'
  
  useEffect(() => {
    const linkedData = sessionStorage.getItem('linked_registration');
    if (linkedData) {
      try {
        const data = JSON.parse(linkedData);
        console.log("Found linked registration data:", data);
        
        // Auto-populate parent info from linked registration
        if (data.parent_given_name) setParentGivenName(data.parent_given_name);
        if (data.parent_last_name) setParentLastName(data.parent_last_name);
        if (data.parent_email) setParentEmail(data.parent_email);
        if (data.parent_phone) setParentPhone(data.parent_phone);
        
        // Set member status - ONLY if explicitly marked as member, not just having a membership number
        if (data.is_scc_member || data.is_section_member) {
          setIsSccMember(true);
        }
        
        // Set member type for discount calculation - check explicit member status
        if (data.is_section_member) {
          setLinkedMemberType('section'); // Football Section member - highest discount
        } else if (data.is_scc_member) {
          setLinkedMemberType('scc_member'); // SCC Club member - club member discount
        } else {
          setLinkedMemberType('non_member'); // Non-member - no discount
        }
        
        // Mark that we have a linked registration (email already used, account exists)
        setHasLinkedRegistration(true);
        
        // Store the linked registration ID for later use
        if (data.linked_registration_id) {
          sessionStorage.setItem('current_linked_registration_id', data.linked_registration_id);
        }
        
        toast.info("Parent info pre-filled. Please complete remaining fields (address, nationality) and continue.");
        
        // Clear the linked_registration data after reading (but keep current_linked_registration_id)
        sessionStorage.removeItem('linked_registration');
      } catch (e) {
        console.error("Error parsing linked registration data:", e);
      }
    }
  }, []);

  // Helper function to round to 2 decimal places (for currency calculations)
  const roundTo2 = (num) => Math.round(num * 100) / 100;

  // Component is remounted on each navigation via wrapper in App.js
  // No explicit reset needed - useState initializes fresh on mount

  // Calculate child age from birthdate
  const calculateAge = (birthdate) => {
    if (!birthdate) return null;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Derive the child's TRUE playing bracket using the calendar year-of-birth
  // convention used by the backend (_age_group_from_birthdate_by_year):
  //   bracket = U(current_year - birth_year)   — no rounding.
  // Example: born 2015 → 2026 - 2015 = 11 → true bracket "U11" (a Dec-2015
  // child is U11 too). Squad/session matching may then step the child UP to
  // the nearest existing bracket at or above this, never below.
  // Returns null when birthdate is missing or produces an out-of-range value.
  const deriveAgeBracket = (birthdate) => {
    if (!birthdate) return null;
    try {
      const birthYear = parseInt(String(birthdate).slice(0, 4), 10);
      if (Number.isNaN(birthYear)) return null;
      const currentYear = new Date().getFullYear();
      const bracket = currentYear - birthYear;
      if (bracket < 4 || bracket > 21) return null;
      return `U${bracket}`;
    } catch {
      return null;
    }
  };

  // Resolve a child's true bracket to the nearest EXISTING session bracket at
  // or ABOVE it (never below) — mirrors the backend bucket step-up rule. When
  // no session bracket exists at/above the child's, falls back to the exact
  // true bracket so numeric-range fallbacks can still match.
  const bracketNum = (b) => {
    if (!b) return null;
    const m = String(b).match(/^[Uu]?(\d{1,2})$/);
    return m ? parseInt(m[1], 10) : null;
  };
  const resolveBracketForChild = (trueBracket) => {
    const n = bracketNum(trueBracket);
    if (n === null) return trueBracket;
    const available = sessions
      .map((s) => bracketNum(s.age_group_name || s.age_group))
      .filter((x) => x !== null && x >= n)
      .sort((a, b) => a - b);
    return available.length ? `U${available[0]}` : trueBracket;
  };

  // Filter sessions appropriate for a child based on age-group bracket AND gender.
  // Fix (Feb 2026): eligibility gate uses the child's year-of-birth bracket
  // (U-code) instead of showing every session. Sessions with an explicit
  // `age_group_name` must match the child's bracket exactly; sessions without
  // one fall back to the min_age/max_age numeric range.
  const getSessionsForChild = (childIndex) => {
    const child = children[childIndex];
    const age = child?.birthdate ? calculateAge(child.birthdate) : null;
    const trueBracket = child?.birthdate ? deriveAgeBracket(child.birthdate) : null;
    // Match against the nearest existing bracket at or above the child's true
    // bracket (play up, never down) — same rule as the backend bucket step-up.
    const bracket = trueBracket ? resolveBracketForChild(trueBracket) : null;
    const childGender = child?.gender || "male";
    
    let filtered = sessions.filter(session => {
      // Session-type filter (unchanged)
      if (sessionTypeFilter !== "all") {
        const sessionType = session.session_type || "training";
        if (sessionTypeFilter === "social" && !["social", "training"].includes(sessionType)) return false;
        if (sessionTypeFilter === "competitive" && !["competitive", "game", "tournament"].includes(sessionType)) return false;
      }
      // Gender filter (unchanged)
      const sessionGender = session.gender || "mixed";
      if (sessionGender !== "mixed" && sessionGender !== childGender) return false;
      
      // Age eligibility — permissive (Aug 2026, fix for the CHENGHAO/
      // ZACHARY reports). A session passes if EITHER:
      //   - its age_group_name matches the child's derived U-bracket, OR
      //   - the child's numeric age falls within the session's min_age/max_age
      // (either alone was sufficient in the pre-Feb-2026 flow — the bracket-
      // strict check regressed parents whose sessions were only numerically
      // tagged.) When the session provides NEITHER a bracket nor a numeric
      // range we let it through — an admin config gap, not a parent problem.
      if (bracket && age !== null) {
        const sessionBracket = session.age_group_name || session.age_group || null;
        const hasBracket = !!sessionBracket;
        const bracketMatches = hasBracket && sessionBracket === bracket;

        const hasNumericMin = Number.isFinite(session.min_age);
        const hasNumericMax = Number.isFinite(session.max_age);
        const hasNumeric = hasNumericMin || hasNumericMax;
        const min = hasNumericMin ? session.min_age : 0;
        const max = hasNumericMax ? session.max_age : 99;
        const numericMatches = hasNumeric && (age >= min && age <= max);

        // If session has neither tag, don't block — admin gap.
        if (hasBracket || hasNumeric) {
          if (!bracketMatches && !numericMatches) return false;
        }
      }
      return true;
    });
    
    // Deduplicate by day+time — if multiple age groups share same slot,
    // show ONE entry and auto-pick the right one for the child's age
    const slotMap = new Map();
    for (const session of filtered) {
      const key = `${session.day_of_week}-${session.start_time}-${session.end_time}-${session.session_type || "training"}`;
      if (!slotMap.has(key)) {
        slotMap.set(key, []);
      }
      slotMap.get(key).push(session);
    }
    
    // For each slot, pick the best session for the child's age (or the first if no age)
    const deduped = [];
    for (const [, slotSessions] of slotMap) {
      if (age !== null && slotSessions.length > 1) {
        // Pick the session whose age range matches the child
        const match = slotSessions.find(s => {
          const min = s.min_age || 0;
          const max = s.max_age || 99;
          return age >= min && age <= max;
        });
        deduped.push(match || slotSessions[0]);
      } else {
        deduped.push(slotSessions[0]);
      }
    }
    
    // Sort by day then time
    return deduped.sort((a, b) => {
      if (a.day_of_week !== b.day_of_week) return a.day_of_week - b.day_of_week;
      return (a.start_time || "").localeCompare(b.start_time || "");
    });
  };

  // Get all unique selected sessions across all children
  const allSelectedSessions = useMemo(() => {
    const allSessions = new Set();
    Object.values(childSessionSelections).forEach(sessionIds => {
      sessionIds.forEach(id => allSessions.add(id));
    });
    return Array.from(allSessions);
  }, [childSessionSelections]);

  // Track which children are enrollable (have age-appropriate sessions available)
  const childrenEnrollmentStatus = useMemo(() => {
    return children.map((child, index) => {
      const availableSessions = getSessionsForChild(index);
      const selectedSessions = childSessionSelections[index] || [];
      return {
        index,
        child,
        hasAvailableSessions: availableSessions.length > 0,
        hasSelectedSessions: selectedSessions.length > 0,
        isEnrollable: selectedSessions.length > 0,
        availableCount: availableSessions.length,
        selectedCount: selectedSessions.length
      };
    });
  }, [children, sessions, childSessionSelections]);

  // Get only children who will be enrolled (have sessions selected)
  const enrollableChildren = useMemo(() => {
    return childrenEnrollmentStatus.filter(c => c.isEnrollable);
  }, [childrenEnrollmentStatus]);

  // Get children with no available sessions for their age
  const ineligibleChildren = useMemo(() => {
    return childrenEnrollmentStatus.filter(c => !c.hasAvailableSessions);
  }, [childrenEnrollmentStatus]);

  // Update children's emergency contact when parent info changes (for new users in Step 1)
  useEffect(() => {
    // Only update if we're a new user and have parent info
    if (!isExistingMember && (parentGivenName || parentLastName || parentPhone)) {
      const parentFullName = `${parentGivenName} ${parentLastName}`.trim();
      
      setChildren(prevChildren => 
        prevChildren.map(child => ({
          ...child,
          // Only update if the field is empty or was set to "Parent" relationship (auto-filled)
          emergency_contact_name: child.emergency_contact_name === "" || child.emergency_relationship === "Parent" 
            ? parentFullName 
            : child.emergency_contact_name,
          emergency_contact: child.emergency_contact === "" || child.emergency_relationship === "Parent"
            ? parentPhone 
            : child.emergency_contact,
          emergency_relationship: child.emergency_relationship === "" ? "Parent" : child.emergency_relationship
        }))
      );
    }
  }, [parentGivenName, parentLastName, parentPhone, isExistingMember]);

  // ==================== DRAFT REGISTRATION FUNCTIONS ====================
  
  // Load draft if draft ID is provided in URL
  useEffect(() => {
    const loadDraft = async () => {
      const draftIdFromUrl = searchParams.get('draft');
      if (draftIdFromUrl) {
        try {
          const response = await axios.get(
            `${API}/academy/draft-registration/${draftIdFromUrl}`,
            { withCredentials: true }
          );
          
          const draft = response.data;
          console.log("Loading draft registration:", draft);
          
          // Restore draft data
          setDraftId(draft.id);
          setHasDraft(true);
          
          // Restore parent info
          if (draft.parent_info) {
            setParentGivenName(draft.parent_info.given_name || "");
            setParentLastName(draft.parent_info.last_name || "");
            setParentEmail(draft.parent_info.email || "");
            setParentPhone(draft.parent_info.phone || "");
            setParentAddress(draft.parent_info.address || "");
            setParentNationality(draft.parent_info.nationality || "");
          }
          
          // Restore children
          if (draft.children && draft.children.length > 0) {
            setChildren(draft.children);
          }
          
          // Restore term selection
          if (draft.selected_term_id) {
            // We'll need to fetch terms first, then select the saved one
            const termsResponse = await axios.get(`${API}/academy/terms`);
            const finalized = termsResponse.data.filter(t => t.is_finalized);
            setTerms(finalized);
            const savedTerm = finalized.find(t => t.id === draft.selected_term_id);
            if (savedTerm) {
              setSelectedTerm(savedTerm);
            }
          }
          
          // Restore session selections
          if (draft.session_selections) {
            setChildSessionSelections(draft.session_selections);
          }
          
          // Restore consents
          if (draft.consents) {
            setConsentPDPA(draft.consents.pdpa || false);
            setConsentSafeSport(draft.consents.safe_sport || false);
            setConsentIndemnity(draft.consents.indemnity || false);
          }
          
          // Restore linked registration info
          if (draft.is_linked_registration) {
            setHasLinkedRegistration(true);
            setLinkedMemberType(draft.linked_member_type);
          }
          
          // Go to saved step
          setStep(draft.current_step || 1);
          
          toast.success("Draft registration loaded successfully!");
          
        } catch (error) {
          console.error("Error loading draft:", error);
          toast.error("Could not load saved draft");
        }
      }
    };
    
    if (!isValidPropUser) {
      loadDraft();
    }
  }, [searchParams]);
  
  // Save draft function
  const saveDraft = async (showToast = true) => {
    // Only save draft for non-logged-in users or if email is provided
    const email = parentEmail || propUser?.email;
    if (!email) {
      if (showToast) toast.error("Please enter your email to save draft");
      return null;
    }
    
    setSavingDraft(true);
    try {
      const draftData = {
        draft_id: draftId,
        email: email,
        current_step: step,
        parent_info: {
          given_name: parentGivenName,
          last_name: parentLastName,
          email: parentEmail,
          phone: parentPhone,
          address: parentAddress,
          nationality: parentNationality,
        },
        children: children,
        selected_term_id: selectedTerm?.id,
        session_selections: childSessionSelections,
        consents: {
          pdpa: consentPDPA,
          safe_sport: consentSafeSport,
          indemnity: consentIndemnity,
        },
        is_linked_registration: hasLinkedRegistration,
        linked_member_type: linkedMemberType,
      };
      
      const response = await axios.post(
        `${API}/academy/draft-registration`,
        draftData,
        { withCredentials: true }
      );
      
      setDraftId(response.data.draft_id);
      setHasDraft(true);
      setLastSaved(new Date());
      
      if (showToast) {
        toast.success("Draft saved! You can resume later using the link sent to your email.");
      }
      
      return response.data.draft_id;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (showToast) toast.error("Failed to save draft");
      return null;
    } finally {
      setSavingDraft(false);
    }
  };
  
  // Auto-save draft when data changes (for non-logged-in users)
  useEffect(() => {
    if (!isExistingMember && parentEmail && parentEmail.includes("@")) {
      // Debounce auto-save
      const timer = setTimeout(() => {
        saveDraft(false); // Silent save
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [step, children, childSessionSelections, selectedTerm, parentGivenName, parentLastName, parentPhone]);
  
  // Send draft reminder email
  const sendDraftReminder = async () => {
    if (!parentEmail) {
      toast.error("Please enter your email first");
      return;
    }
    
    try {
      await axios.post(`${API}/academy/send-draft-reminder`, {
        email: parentEmail,
      });
      toast.success("If you have a saved draft, a reminder email has been sent.");
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast.error("Failed to send reminder");
    }
  };

  // ==================== END DRAFT REGISTRATION FUNCTIONS ====================

  useEffect(() => {
    if (step === 2) {
      fetchTerms();
    }
  }, [step]);

  useEffect(() => {
    if (selectedTerm) {
      fetchSessions();
      fetchRateTableInfo();
      checkPendingRegistrationForTerm(selectedTerm.id);
    }
  }, [selectedTerm]);

  // Duplicate-guard resume check: if this parent already has a live pending
  // registration for the selected term, we ask them "continue where you left
  // off vs. start fresh" before they progress further. Silent no-op for
  // guests (endpoint 401) and when nothing pending exists.
  const checkPendingRegistrationForTerm = async (termId) => {
    if (!termId || resumeCheckDismissed) return;
    try {
      const res = await axios.get(
        `${API}/academy/registrations/mine/pending?term_id=${termId}`,
        { withCredentials: true },
      );
      if (res.data?.found && res.data?.registration) {
        setPendingResumeReg(res.data.registration);
      } else {
        setPendingResumeReg(null);
      }
    } catch (err) {
      // 401 = not logged in yet (guest flow), that's fine — no resume.
      // Any other error we swallow so the wizard doesn't block.
      if (err.response?.status !== 401) {
        console.debug("pending-registration check failed:", err.response?.status);
      }
      setPendingResumeReg(null);
    }
  };

  const handleResumeContinue = () => {
    // Continue-where-you-left-off: the wizard is already prefilled from the
    // parent's saved draft (see `restoreDraft` effect). We just close the
    // modal — on submit, the server's reuse rule will match this reg and
    // update it in place.
    setPendingResumeReg(null);
    setResumeCheckDismissed(true);
    toast.success("Continuing your existing registration");
  };

  const handleResumeStartFresh = async () => {
    if (!pendingResumeReg?.id) return;
    setSupersedingPending(true);
    try {
      await axios.post(
        `${API}/academy/registrations/${pendingResumeReg.id}/supersede`,
        {},
        { withCredentials: true },
      );
      toast.success("Previous registration cancelled — starting fresh");
      setPendingResumeReg(null);
      setResumeCheckDismissed(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel the previous registration");
    } finally {
      setSupersedingPending(false);
    }
  };

  // Check email availability when email changes (with debounce) - ONLY for new users
  useEffect(() => {
    // Skip email check for existing members OR linked registrations (account already created)
    if (isExistingMember || hasLinkedRegistration) {
      setEmailError("");
      return;
    }
    
    const timer = setTimeout(() => {
      if (parentEmail && parentEmail.includes("@")) {
        checkEmailAvailability(parentEmail);
      } else {
        setEmailError("");
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [parentEmail, isExistingMember, hasLinkedRegistration]);

  const checkEmailAvailability = async (email) => {
    try {
      const response = await axios.post(`${API}/academy/check-email`, { email });
      if (!response.data.available) {
        const { action, has_pending_payment, pending_payment, message } = response.data;
        
        if (has_pending_payment) {
          // User has a pending payment - show specific guidance
          setEmailError(
            `${message} Reference: ${pending_payment?.reference || 'N/A'}, Amount: $${pending_payment?.amount?.toFixed(2) || '0.00'}`
          );
          setEmailAction(action);
          setPendingPaymentInfo(pending_payment);
        } else {
          // Regular email exists error
          setEmailError(message || "This email is already registered. Please login to your existing account.");
          setEmailAction(action || "login");
          setPendingPaymentInfo(null);
        }
      } else {
        setEmailError("");
        setEmailAction(null);
        setPendingPaymentInfo(null);
      }
    } catch (error) {
      console.error("Error checking email:", error);
    }
  };

  // Soft duplicate check against existing accounts (phone / parent name / child name+DOB).
  // Runs on step 1 for non-logged-in users. Fail-open: errors don't block.
  useEffect(() => {
    if (isExistingMember || hasLinkedRegistration || duplicateDismissed || step !== 1) return;

    const hasEnoughToCheck =
      (parentPhone && parentPhone.replace(/\D/g, "").length >= 6) ||
      (parentGivenName && parentLastName && parentGivenName.length >= 2 && parentLastName.length >= 2) ||
      children.some(c => c.given_name && c.last_name && c.birthdate);

    if (!hasEnoughToCheck) {
      setDuplicateMatches([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const payload = {
          phone: parentPhone,
          parent_given_name: parentGivenName,
          parent_last_name: parentLastName,
          children: children
            .filter(c => c.given_name && c.last_name && c.birthdate)
            .map(c => ({ given_name: c.given_name, last_name: c.last_name, birthdate: c.birthdate }))
        };
        const resp = await axios.post(`${API}/academy/check-duplicates`, payload);
        setDuplicateMatches(resp.data?.matches || []);
      } catch (e) {
        // fail-open
        console.warn("Duplicate check failed:", e?.response?.status);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [parentPhone, parentGivenName, parentLastName, children, step, isExistingMember, hasLinkedRegistration, duplicateDismissed]);

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms`);
      // Filter to only show finalized terms that are NOT hidden
      const availableTerms = response.data.filter(t => t.is_finalized && !t.is_hidden);
      setTerms(availableTerms);
      // If the URL carried ?term_id=X (from the Parent Dashboard resume link),
      // auto-select it so the wizard lands directly on that term's sessions.
      const urlTermId = searchParams.get("term_id");
      if (urlTermId) {
        const match = availableTerms.find(t => t.id === urlTermId);
        if (match) setSelectedTerm(match);
      }
    } catch (error) {
      console.error("Error fetching terms:", error);
      toast.error("Failed to load terms");
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms/${selectedTerm.id}/sessions`);
      setSessions(response.data);
      // Reset child session selections when sessions change
      setChildSessionSelections({});
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Failed to load sessions");
    }
  };

  const fetchRateTableInfo = async () => {
    try {
      // Prefer the term's frozen rate_table_snapshot — that's exactly what the
      // BACKEND prices from, so the parent's on-screen quote matches the
      // invoice. Finalized terms often carry only a snapshot (no resolvable
      // rate_table_id); without this we'd leave rateTable=null and silently
      // fall back to the raw session.price (e.g. $47 instead of a
      // max-capacity $45).
      if (selectedTerm?.rate_table_snapshot) {
        setRateTable(selectedTerm.rate_table_snapshot);
      } else if (selectedTerm?.rate_table_id) {
        const response = await axios.get(`${API}/academy/rate-tables`);
        const table = response.data.find(t => t.id === selectedTerm.rate_table_id);
        setRateTable(table);
      }

      if (selectedTerm?.rate_table_id) {
        const itemsResponse = await axios.get(
          `${API}/academy/optional-items?rate_table_id=${selectedTerm.rate_table_id}`
        );
        setOptionalItems(itemsResponse.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching rate table:", error);
    }
  };

  const calculateFees = async () => {
    try {
      setLoading(true);
      
      // Get digital membership pricing
      const dmPriceResponse = await axios.post(
        `${API}/academy/calculate-digital-membership`,
        { 
          rate_table_id: selectedTerm?.rate_table_id,
          term_start_date: selectedTerm?.start_date,
          term_end_date: selectedTerm?.end_date
        }
      );

      // Calculate term weeks with PRO-RATA for mid-term registrations
      let termWeeks = selectedTerm?.duration_unit === "weeks" 
        ? (selectedTerm?.duration_value || 10)
        : Math.ceil((selectedTerm?.duration_value || 70) / 7);
      
      let isProrated = false;
      let weeksElapsed = 0;
      let fullTermWeeks = termWeeks;
      let disableVolumeDiscount = false; // True if ≥70% of term passed (no volume stacking)
      const LATE_REGISTRATION_THRESHOLD = 0.70; // 70% of term
      
      // Calculate remaining weeks if term has started
      if (selectedTerm?.start_date) {
        const termStart = new Date(selectedTerm.start_date);
        const today = new Date();
        const daysSinceStart = Math.floor((today - termStart) / (1000 * 60 * 60 * 24));
        
        if (daysSinceStart > 0) {
          weeksElapsed = Math.floor(daysSinceStart / 7);
          const remainingWeeks = Math.max(0, fullTermWeeks - weeksElapsed);
          const percentElapsed = weeksElapsed / fullTermWeeks;
          
          if (remainingWeeks < fullTermWeeks && remainingWeeks > 0) {
            termWeeks = remainingWeeks;
            isProrated = true;
            
            // If ≥70% of term passed, disable volume discount (but keep member/sibling discounts)
            if (percentElapsed >= LATE_REGISTRATION_THRESHOLD) {
              disableVolumeDiscount = true;
              console.log("LATE REGISTRATION (≥70% elapsed) - Volume discount disabled:", {
                weeksElapsed,
                fullTermWeeks,
                percentElapsed: `${(percentElapsed * 100).toFixed(1)}%`,
                remainingWeeks: termWeeks
              });
            } else {
              console.log("PRO-RATA APPLIED (< 70% elapsed) - Full discounts apply:", {
                weeksElapsed,
                fullTermWeeks,
                percentElapsed: `${(percentElapsed * 100).toFixed(1)}%`,
                remainingWeeks: termWeeks
              });
            }
          } else if (remainingWeeks <= 0) {
            // Term has ended
            toast.error("This term has already ended. Please select a different term.");
            return;
          }
        }
      }
      
      // Calculate fees ONLY for children with sessions selected (enrolled children)
      let totalBaseFee = 0;
      let totalOriginalFee = 0; // Before any volume/session discounts
      const enrolledChildrenCount = enrollableChildren.length;
      
      // Get pricing mode from rate table
      const pricingMode = rateTable?.pricing_mode || "progressive";
      const volumeTiers = rateTable?.volume_pricing_tiers || [];
      
      // Get membership discount
      // For linked registrations (hasLinkedRegistration=true), use linkedMemberType
      // For logged-in users, use loggedInUser.member_type
      const memberType = loggedInUser?.member_type || linkedMemberType;
      let memberDiscountPercent = 0;

      // ─────────── Active-DM detection ───────────
      // If the parent already bought Digital Membership in a prior term and it's
      // still valid, auto-apply the DM discount and DO NOT offer DM for re-purchase.
      const dmExpiryIso = loggedInUser?.digital_membership_expires_at;
      const hasActiveDM = !!(
        loggedInUser?.is_digital_member &&
        (!dmExpiryIso || new Date(dmExpiryIso) > new Date())
      );

      let memberDiscountSource = null; // 'section' | 'club' | 'digital'
      if (memberType === 'section' && rateTable?.membership_discounts?.section_member?.value > 0) {
        // Football Section member - highest discount
        memberDiscountPercent = rateTable.membership_discounts.section_member.value;
        memberDiscountSource = 'section';
      } else if (memberType === 'digital' && rateTable?.membership_discounts?.digital_member?.value > 0) {
        // Explicitly a Digital Member. Some legacy records also carry
        // is_scc_member=true, which used to mislabel this as a Club discount.
        memberDiscountPercent = rateTable.membership_discounts.digital_member.value;
        memberDiscountSource = 'digital';
      } else if ((memberType === 'scc_member' || memberType === 'club_member'
                  || loggedInUser?.is_scc_member || isSccMember)
                 && rateTable?.membership_discounts?.club_member?.value > 0) {
        // Club member (SCC member but not football section).
        // `isSccMember` covers new registrations where the parent has ticked
        // "I am an SCC Member" + entered a valid membership number — they don't
        // have a `loggedInUser` yet but the discount must still apply in the
        // wizard preview, otherwise they see the full price and the PayNow QR
        // ends up generated against the un-discounted amount.
        memberDiscountPercent = rateTable.membership_discounts.club_member.value;
        memberDiscountSource = 'club';
      } else if (hasActiveDM && rateTable?.membership_discounts?.digital_member?.value > 0) {
        // Existing Digital Member — apply their DM discount automatically without charging
        // the annual fee again. They already paid in a prior term.
        memberDiscountPercent = rateTable.membership_discounts.digital_member.value;
        memberDiscountSource = 'digital';
      }
      
      // FIRST: Determine sibling discount BEFORE calculating per-child fees
      // This needs to be calculated upfront so we can apply it to per-session prices.
      //
      // SIBLING-TIER RULE (hybrid, backend is the source of truth):
      //   • Before term start: total = cart_children + children this parent has
      //     ALREADY submitted in any earlier registration for the same term
      //     (deduped by player_id). This lets a parent enrolling siblings
      //     across multiple submissions still get sibling-tier pricing.
      //   • On/after term start: total = cart_children only. Late siblings
      //     don't retroactively change earlier submissions' tier.
      //
      // The backend `/academy/sibling-tier-count` endpoint is authoritative —
      // we POST our cart's stable child keys (existing_child_id when known,
      // otherwise a name+DOB hash) and it returns the deduped total.
      let siblingDiscountPercent = 0;
      const siblingDiscountsEnabled = rateTable?.sibling_discounts_enabled !== false;
      const siblingDiscountsAvailable = rateTable?.sibling_discounts?.length > 0;
      const cartChildKeys = enrollableChildren.map((c) => {
        // `enrollableChildren` items are wrappers `{ index, child, ... }`
        // (see the useMemo above). The child fields live on `c.child.*`,
        // not on `c` directly. Reading them off the wrapper produced empty
        // `name:||` keys for every child, so the sibling-tier API received
        // garbage and silently fell back to cart-only counts — which under-
        // billed multi-child returning parents who submitted siblings across
        // separate registrations for the same term (the hybrid-rule case).
        const child = c.child || c;
        if (child.existing_child_id) return `id:${child.existing_child_id}`;
        const g = (child.given_name || "").trim().toLowerCase();
        const l = (child.last_name || "").trim().toLowerCase();
        const dob = (child.birthdate || "").trim();
        return `name:${g}|${l}|${dob}`;
      }).filter(Boolean);

      let totalSiblingCount = enrolledChildrenCount;
      let siblingTierMeta = { rule: "cart_only_no_parent_yet", prior_count_used: 0, term_started: null };
      // P0 fix (May 2026): the previous code referenced an undeclared
      // identifier inside this calculation function. For anonymous parents
      // (or any case where loggedInUser hadn't loaded yet) JavaScript
      // evaluated the bare identifier and threw a ReferenceError, which the
      // outer try/catch surfaced as "Failed to calculate fees" — blocking
      // Step 3 entirely. The submit handler declares its own local for the
      // parent id; that local does NOT leak into this scope. We simply rely
      // on loggedInUser?.id and skip the sibling-tier API call when it's
      // not available (anonymous parents can't have prior-term siblings).
      if (siblingDiscountsEnabled && siblingDiscountsAvailable && loggedInUser?.id && selectedTerm?.id) {
        try {
          const stRes = await axios.get(`${API}/academy/sibling-tier-count`, {
            params: {
              parent_id: loggedInUser.id,
              term_id: selectedTerm.id,
              cart_child_keys: cartChildKeys.join(","),
            },
            withCredentials: true,
          });
          totalSiblingCount = stRes.data?.total_sibling_count ?? enrolledChildrenCount;
          siblingTierMeta = stRes.data || siblingTierMeta;
        } catch (siblingErr) {
          console.warn("sibling-tier-count fetch failed, falling back to cart count:", siblingErr?.response?.status);
          totalSiblingCount = enrolledChildrenCount;
        }
      }

      if (siblingDiscountsEnabled && totalSiblingCount > 1 && siblingDiscountsAvailable) {
        const sortedDiscounts = [...rateTable.sibling_discounts].sort(
          (a, b) => b.child_number - a.child_number
        );
        const applicableDiscount = sortedDiscounts.find(d => totalSiblingCount >= d.child_number);
        if (applicableDiscount && applicableDiscount.type === "percentage") {
          siblingDiscountPercent = applicableDiscount.value;
        }
      }
      
      console.log("Sibling discount calculated upfront:", {
        enrolledChildrenCount,
        totalSiblingCount,
        siblingDiscountPercent,
        siblingTierMeta,
        siblingDiscountsEnabled,
        siblingDiscountsAvailable,
      });
      
      // Per-position sibling logic (Aug 2026 fix). The sibling discount must
      // apply PER CHILD BY RANK — child #1 never gets it, #2+ get the tier —
      // matching the backend rule (calculate_fee_with_discounts: child_index>=2)
      // and AdminRegisterForParent. `priorSiblingOffset` accounts for the
      // hybrid rule where siblings submitted earlier this term push the cart's
      // children up a rank.
      const siblingTiersAsc = (siblingDiscountsEnabled && rateTable?.sibling_discounts)
        ? [...rateTable.sibling_discounts].sort((a, b) => a.child_number - b.child_number)
        : [];
      const priorSiblingOffset = Math.max(0, totalSiblingCount - enrolledChildrenCount);
      const siblingPctForPosition = (enrollPosition) => {
        if (enrollPosition < 2 || siblingTiersAsc.length === 0) return 0;
        let pct = 0;
        for (const t of siblingTiersAsc) {
          if (enrollPosition >= t.child_number && t.type === "percentage") pct = t.value;
        }
        return pct;
      };

      // ── Cancelled single sessions (Jun 2026) ───────────────────────────
      // A specific date cancelled by the academy (e.g. one Saturday in Nov)
      // is simply NOT billed — only for the weekday it falls on. Mirrors the
      // backend (count_cancelled_sessions_for_day) so the quote matches the
      // invoice, and we name the dates so parents see why the total dropped.
      // Only dates inside the billed window: for a pro-rated (mid-term) quote
      // we bill the remaining weeks, so only future cancellations count.
      const cancelledDates = (selectedTerm?.cancelled_dates || [])
        .map((c) => (typeof c === "string" ? c : c?.date))
        .filter(Boolean)
        .map((d) => new Date(`${String(d).slice(0, 10)}T00:00:00`))
        .filter((d) => !isNaN(d) && (!isProrated || d >= new Date(new Date().toDateString())));
      const isoDow = (d) => (d.getDay() === 0 ? 7 : d.getDay());
      const cancelledDatesForDay = (dow) =>
        cancelledDates.filter((d) => isoDow(d) === Number(dow));
      const friendlyDate = (d) =>
        d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });

      // Per-child breakdown for display
      const childBreakdowns = [];
      const cancellationNotes = [];
      let totalCancellationOffset = 0;
      let enrollIdx = 0;
      
      children.forEach((child, childIndex) => {
        const childSessions = childSessionSelections[childIndex] || [];
        // Skip children with no sessions selected
        if (childSessions.length === 0) return;
        enrollIdx += 1;
        // Rank of THIS child within the family's siblings (1-based, incl. prior).
        const childSiblingPct = siblingPctForPosition(enrollIdx + priorSiblingOffset);
        
        const sessionCount = childSessions.length;
        let childWeeklyFee = 0;
        let childOriginalWeeklyFee = 0;
        let pricePerSession = 0;
        let priceAfterSiblingDiscount = 0;
        let originalPricePerSession = 0;
        const sessionDetails = [];
        
        if (pricingMode === "max_capacity") {
          // FIXED TERM (MAX CAPACITY) — Jun 2026
          // Parent pays for max_sessions_per_week × weeks × max_capacity_price
          // regardless of how many sessions they pick. Sibling + member
          // discounts still apply on top. Prevents "book 1 attend 3" freeloading.
          const mcPrice = parseFloat(rateTable?.max_capacity_price_per_session) || 0;
          const mcMaxSessions = parseInt(rateTable?.max_sessions_per_week) || 0;
          if (mcPrice <= 0 || mcMaxSessions <= 0) {
            toast.error("Max Capacity pricing not fully configured. Please contact admin.");
            return;
          }

          originalPricePerSession = roundTo2(mcPrice);
          pricePerSession = originalPricePerSession;

          // Sibling discount applies to the per-session rate (per-child rank)
          priceAfterSiblingDiscount = childSiblingPct > 0
            ? roundTo2(pricePerSession * (1 - childSiblingPct / 100))
            : pricePerSession;

          // Line-items reflect the parent's picked sessions, but the BILLED
          // sessions/week is the max (mcMaxSessions), not sessionCount.
          childSessions.forEach((sessId) => {
            const sess = sessions.find(s => s.id === sessId);
            sessionDetails.push({
              id: sessId,
              day: sess?.day_of_week,
              time: `${sess?.start_time}-${sess?.end_time}`,
              originalPrice: originalPricePerSession,
              priceBeforeSibling: pricePerSession,
              finalPrice: priceAfterSiblingDiscount,
            });
          });

          // Weekly fee always = mcMaxSessions × per-session price, regardless of pick count
          childWeeklyFee = roundTo2(priceAfterSiblingDiscount * mcMaxSessions);
          childOriginalWeeklyFee = roundTo2(originalPricePerSession * mcMaxSessions);
        } else if (pricingMode === "volume" && volumeTiers.length > 0) {
          // Volume-based pricing: all sessions at same rate based on count
          const sortedTiers = [...volumeTiers].sort((a, b) => b.min_sessions - a.min_sessions);
          
          // Get single-session tier (no volume discount) - MUST exist in rate table
          const singleTier = volumeTiers.find(t => t.min_sessions === 1);
          if (!singleTier) {
            toast.error("Pricing not configured for this term. Please contact admin.");
            return;
          }
          const singleSessionRate = roundTo2(singleTier.price_per_session);
          
          // Determine which tier to use
          let applicableTier;
          if (disableVolumeDiscount) {
            // Late registration (≥70% elapsed): use single-session rate (no volume discount)
            applicableTier = singleTier;
            pricePerSession = singleSessionRate;
            console.log("LATE REGISTRATION - Using single-session rate:", singleSessionRate);
          } else {
            // Normal or early pro-rata: use volume tier
            applicableTier = sortedTiers.find(t => sessionCount >= t.min_sessions);
            pricePerSession = roundTo2(applicableTier?.price_per_session || singleSessionRate);
          }
          
          originalPricePerSession = singleSessionRate; // For comparison
          
          // Apply sibling discount to per-session price (per-child rank; always applies for #2+)
          priceAfterSiblingDiscount = childSiblingPct > 0 
            ? roundTo2(pricePerSession * (1 - childSiblingPct / 100))
            : pricePerSession;
          
          console.log(`PRICING DEBUG for ${child.given_name}:`, {
            sessionCount,
            disableVolumeDiscount,
            applicableTier,
            singleSessionRate,
            pricePerSession,
            siblingDiscountPercent: childSiblingPct,
            priceAfterSiblingDiscount,
            calculation: childSiblingPct > 0 
              ? `${pricePerSession} × (1 - ${childSiblingPct}/100) = ${priceAfterSiblingDiscount}`
              : `No sibling discount applied`
          });
          
          childSessions.forEach((sessId) => {
            const sess = sessions.find(s => s.id === sessId);
            sessionDetails.push({
              id: sessId,
              day: sess?.day_of_week,
              time: `${sess?.start_time}-${sess?.end_time}`,
              originalPrice: originalPricePerSession,
              priceBeforeSibling: pricePerSession,
              finalPrice: priceAfterSiblingDiscount
            });
          });
          
          childWeeklyFee = roundTo2(priceAfterSiblingDiscount * sessionCount);
          childOriginalWeeklyFee = roundTo2(originalPricePerSession * sessionCount);
        } else {
          // Progressive pricing
          const sessionDiscounts = rateTable?.session_count_discounts || [];
          
          childSessions.forEach((sessId, sessionIndex) => {
            const sess = sessions.find(s => s.id === sessId);
            let sessionPrice = sess?.price || 0;
            const originalPrice = sessionPrice;
            
            // Apply progressive discount for 2nd, 3rd, etc sessions (ONLY if volume discount NOT disabled)
            if (!disableVolumeDiscount && sessionIndex > 0) {
              const discountConfig = sessionDiscounts.find(d => d.session_count === sessionIndex + 1);
              if (discountConfig && discountConfig.type === "percentage") {
                sessionPrice = roundTo2(sessionPrice * (1 - discountConfig.value / 100));
              }
            }
            
            // Apply sibling discount on top (per-child rank; applies for #2+)
            const priceAfterSibling = childSiblingPct > 0
              ? roundTo2(sessionPrice * (1 - childSiblingPct / 100))
              : sessionPrice;
            
            sessionDetails.push({
              id: sessId,
              day: sess?.day_of_week,
              time: `${sess?.start_time}-${sess?.end_time}`,
              originalPrice: roundTo2(originalPrice),
              priceBeforeSibling: roundTo2(sessionPrice),
              finalPrice: roundTo2(priceAfterSibling)
            });
            
            childWeeklyFee += roundTo2(priceAfterSibling);
            childOriginalWeeklyFee += roundTo2(originalPrice);
          });
          
          pricePerSession = sessionCount > 0 ? roundTo2(childWeeklyFee / sessionCount) : 0;
          originalPricePerSession = sessionCount > 0 ? roundTo2(childOriginalWeeklyFee / sessionCount) : 0;
          priceAfterSiblingDiscount = pricePerSession; // Already has sibling discount applied
        }
        
        // Apply member discount - round all intermediate values
        // Cancelled single dates are removed from the billed weeks of THEIR
        // weekday only, before any member discount.
        let childCancellationOffset = 0;
        sessionDetails.forEach((sd) => {
          const hits = cancelledDatesForDay(sd.day);
          if (hits.length === 0) return;
          childCancellationOffset += roundTo2((sd.finalPrice || 0) * hits.length);
          hits.forEach((h) => {
            const label = friendlyDate(h);
            if (!cancellationNotes.includes(label)) cancellationNotes.push(label);
          });
        });
        childCancellationOffset = roundTo2(childCancellationOffset);
        totalCancellationOffset = roundTo2(totalCancellationOffset + childCancellationOffset);

        const childTermFee = roundTo2(childWeeklyFee * termWeeks - childCancellationOffset);
        const childOriginalTermFee = roundTo2(childOriginalWeeklyFee * termWeeks);
        const memberDiscount = roundTo2(childTermFee * (memberDiscountPercent / 100));
        const childFinalFee = roundTo2(childTermFee - memberDiscount);
        // Per-child sibling saving (sibling is the last multiplicative factor
        // before the member discount, so pre-sibling = childTermFee/(1-pct/100)).
        const childSiblingSavings = childSiblingPct > 0
          ? roundTo2(childTermFee * childSiblingPct / (100 - childSiblingPct))
          : 0;
        
        childBreakdowns.push({
          childIndex,
          name: `${child.given_name} ${child.last_name}`,
          sessionCount,
          sessions: sessionDetails,
          weeklyFee: childWeeklyFee,
          originalWeeklyFee: childOriginalWeeklyFee,
          pricePerSession,
          originalPricePerSession,
          termFee: childTermFee,
          cancellationOffset: childCancellationOffset,
          originalTermFee: childOriginalTermFee,
          memberDiscount,
          memberDiscountPercent,
          siblingDiscount: childSiblingSavings,
          siblingDiscountPercent: childSiblingPct,
          finalFee: childFinalFee,
          // Max Capacity flag + billed sessions/week so the summary can show
          // "$45 × 2 sessions/wk × 9 wks" clearly instead of the misleading
          // "$94/session × 9 weeks" that was there before.
          isMaxCapacity: pricingMode === "max_capacity",
          billedSessionsPerWeek: pricingMode === "max_capacity"
            ? (parseInt(rateTable?.max_sessions_per_week) || 0)
            : sessionCount,
        });
        
        totalBaseFee += childFinalFee;
        totalOriginalFee += childOriginalTermFee;
      });

      // Registration fee is a one-time-per-child charge.
      // Parent controls per-child via the "Charge registration fee" checkbox on
      // each child card. Default true for new registrations; parent unchecks if
      // the child has already paid (e.g. legacy academy player pre-portal).
      // Admin verifies manually at payment approval.
      const registrationFeePerChild = rateTable?.registration_fee?.enabled ?
        rateTable.registration_fee.amount : 0;
      const registrationFeeEnabled = rateTable?.registration_fee?.enabled && registrationFeePerChild > 0;

      // Count only enrolled children whose parent has left the fee checkbox ON
      const chargeableChildrenCount = enrollableChildren.filter(
        ec => ec.child?.charge_registration_fee !== false
      ).length;
      const newEnrolledChildrenCount = chargeableChildrenCount; // alias for existing UI code
      const registrationFee = (registrationFeeEnabled && !skipRegistrationFee) ?
        (registrationFeePerChild * chargeableChildrenCount) : 0;

      // Optional items total
      let optionalItemsTotal = 0;
      selectedOptionalItems.forEach(itemId => {
        const item = optionalItems.find(i => i.id === itemId);
        if (item) {
          optionalItemsTotal += item.base_price;
        }
      });

      // Digital Membership - $126 for 1 FULL YEAR
      const dmPrice = dmPriceResponse.data.price || 126;
      const dmExpiresAt = dmPriceResponse.data.expires_at;
      const dmCoverageInfo = dmPriceResponse.data.coverage_info;

      // Calculate DM discount - use rate table value, fallback to 0 if not set
      const dmDiscountPercent = rateTable?.membership_discounts?.digital_member?.value ?? 0;
      // Always calculate potential savings (for display), actual savings only if DM selected
      const potentialSavingsThisTerm = totalBaseFee * (dmDiscountPercent / 100);
      const savingsThisTerm = includeDM ? roundTo2(potentialSavingsThisTerm) : 0;
      const projected4TermSavings = roundTo2(potentialSavingsThisTerm * 4); // Always show potential
      const netBenefit = roundTo2(projected4TermSavings - dmPrice);

      // Sibling discount is ALREADY applied to per-session prices above
      // We just need to track it for display purposes
      // Calculate the total amount saved from sibling discount
      // Sum of the ACTUAL per-child sibling savings (child #1 contributes 0).
      // Previously this was totalOriginalFee - totalBaseFee, which wrongly
      // folded the membership discount into the "sibling" figure.
      let siblingDiscountSavings = roundTo2(
        childBreakdowns.reduce((s, cb) => s + (cb.siblingDiscount || 0), 0)
      );
      
      console.log("Sibling discount applied (already in base prices):", {
        enrolledChildrenCount,
        siblingDiscountPercent,
        siblingDiscountSavings,
        totalOriginalFee,
        totalBaseFee
      });

      // Calculate totals (before card fee) - sibling discount is already in totalBaseFee
      const subtotalBeforeDiscounts = roundTo2(totalBaseFee + registrationFee + optionalItemsTotal);
      let totalWithoutDM = roundTo2(subtotalBeforeDiscounts);
      let totalWithDM = roundTo2((totalBaseFee - savingsThisTerm) + registrationFee + optionalItemsTotal + dmPrice);

      // Apply discount code if validated
      let discountCodeAmount = 0;
      let discountCodeDisplay = null;
      
      if (validatedDiscount) {
        if (validatedDiscount.discount_type === "member_rate") {
          // Member rate discount - recalculate with member discount if not already a member
          // This is handled by showing member rates, so we just indicate it
          discountCodeDisplay = "Member rates applied";
        } else if (validatedDiscount.discount_type === "percentage") {
          discountCodeAmount = roundTo2(totalWithoutDM * (validatedDiscount.discount_value / 100));
          totalWithoutDM = roundTo2(totalWithoutDM - discountCodeAmount);
          const dmDiscountAmount = roundTo2(totalWithDM * (validatedDiscount.discount_value / 100));
          totalWithDM = roundTo2(totalWithDM - dmDiscountAmount);
          discountCodeDisplay = `${validatedDiscount.discount_value}% off`;
        } else if (validatedDiscount.discount_type === "fixed_amount") {
          discountCodeAmount = roundTo2(validatedDiscount.discount_value);
          totalWithoutDM = roundTo2(Math.max(0, totalWithoutDM - discountCodeAmount));
          totalWithDM = Math.max(0, totalWithDM - discountCodeAmount);
          discountCodeDisplay = `$${validatedDiscount.discount_value} off`;
        }
      }

      // ── Cancelled-session credit (auto-applied at checkout) ───────────
      // A session cancelled AFTER the family paid leaves a monetary credit on
      // the child's account. It is consumed by this invoice server-side, so
      // show it here with the reason instead of an unexplained lower total.
      let creditApplied = 0;
      let creditAppliedWithDM = 0;
      let creditLabel = "";
      let creditNotes = [];
      const existingChildIds = enrollableChildren
        .map((ec) => (ec.child || ec).existing_child_id)
        .filter(Boolean);
      // No existing_child_id resolved (a returning parent whose child was
      // re-keyed, or a duplicate record) used to skip the lookup entirely, so
      // the credit silently vanished from checkout. Ask anyway — the server
      // pools the family's credit and will apply the same figure.
      if (loggedInUser?.id) {
        try {
          const crRes = await axios.get(`${API}/academy/my-cancellation-credit`, {
            params: existingChildIds.length > 0
              ? { child_ids: existingChildIds.join(",") }
              : {},
            withCredentials: true,
          });
          const pool = Number(crRes.data?.available || 0);
          if (pool > 0) {
            creditNotes = crRes.data?.notes || [];
            creditLabel = crRes.data?.label || "Cancelled-session credit applied";
            creditApplied = roundTo2(Math.min(pool, totalWithoutDM));
            creditAppliedWithDM = roundTo2(Math.min(pool, totalWithDM));
            totalWithoutDM = roundTo2(Math.max(0, totalWithoutDM - creditApplied));
            totalWithDM = roundTo2(Math.max(0, totalWithDM - creditAppliedWithDM));
          }
        } catch (creditErr) {
          console.warn("cancellation credit lookup failed:", creditErr?.response?.status);
        }
      }

      // Card payment fee — canonical gross-up via the backend engine
      // (recovers Stripe's 3.4% + $0.50). Routed through
      // canonical_fee_engine.gross_up_total so the displayed card fee + total
      // are byte-identical to the authoritative Stripe charge.
      let cardFeeWithoutDM, cardTotalWithoutDM, cardFeeWithDM, cardTotalWithDM;
      try {
        const cfResp = await axios.post(`${API}/academy/checkout-card-fee`, {
          subtotals: [totalWithoutDM, totalWithDM],
          payment_method: "credit_card",
        });
        const [r0, r1] = cfResp.data.results;
        cardFeeWithoutDM = r0.card_fee;
        cardTotalWithoutDM = r0.total;
        cardFeeWithDM = r1.card_fee;
        cardTotalWithDM = r1.total;
      } catch (e) {
        // Fallback: same gross-up formula computed locally.
        const gu = (s) => roundTo2((s + 0.5) / (1 - 0.034));
        cardTotalWithoutDM = gu(totalWithoutDM);
        cardFeeWithoutDM = roundTo2(cardTotalWithoutDM - totalWithoutDM);
        cardTotalWithDM = gu(totalWithDM);
        cardFeeWithDM = roundTo2(cardTotalWithDM - totalWithDM);
      }
      
      // Calculate total member discount
      const totalMemberDiscount = roundTo2(childBreakdowns.reduce((sum, cb) => sum + cb.memberDiscount, 0));

      setFeeCalculation({
        base_fee: roundTo2(totalBaseFee),
        original_fee: roundTo2(totalOriginalFee), // Before volume/session discounts and member discount
        children_count: enrolledChildrenCount,
        total_children: children.length,
        term_weeks: termWeeks,
        full_term_weeks: fullTermWeeks,
        weeks_elapsed: weeksElapsed,
        is_prorated: isProrated,
        late_registration: disableVolumeDiscount, // True if ≥70% elapsed
        volume_discount_disabled: disableVolumeDiscount,
        child_breakdowns: childBreakdowns, // Per-child breakdown
        cancellation_offset_total: roundTo2(totalCancellationOffset),
        cancellation_notes: cancellationNotes,
        credit_applied: creditApplied,
        credit_applied_with_dm: creditAppliedWithDM,
        credit_label: creditLabel,
        credit_notes: creditNotes,
        member_discount_percent: memberDiscountPercent,
        member_discount_source: memberDiscountSource,
        member_discount_total: totalMemberDiscount,
        registration_fee: roundTo2(registrationFee),
        registration_fee_enabled: registrationFeeEnabled, // Whether reg fee is configured
        registration_fee_per_child: roundTo2(registrationFeePerChild), // Store per-child amount for display
        registration_fee_skipped: skipRegistrationFee, // Track if parent opted out
        optional_items_total: roundTo2(optionalItemsTotal),
        dm_enabled: rateTable?.digital_membership_config?.enabled || false,
        dm_price: roundTo2(dmPrice),
        dm_expires_at: dmExpiresAt,
        dm_coverage_info: dmCoverageInfo,
        dm_discount_percent: dmDiscountPercent,
        has_active_dm: hasActiveDM,
        active_dm_expires_at: hasActiveDM ? dmExpiryIso : null,
        potential_savings_this_term: roundTo2(potentialSavingsThisTerm), // Always show potential savings
        savings_this_term: roundTo2(savingsThisTerm), // Actual savings (only if DM selected)
        projected_4_term_savings: projected4TermSavings,
        net_benefit: netBenefit,
        sibling_discount: siblingDiscountSavings, // Amount saved from sibling discount (already in prices)
        sibling_discount_percent: siblingDiscountPercent,
        sibling_count_total: totalSiblingCount,
        // Hybrid-rule diagnostic fields (populated when backend sibling-tier-count was used)
        sibling_prior_count_used: siblingTierMeta?.prior_count_used || 0,
        sibling_term_started: siblingTierMeta?.term_started ?? null,
        sibling_rule: siblingTierMeta?.rule || null,
        // Names of existing siblings already enrolled in this term that contributed
        // to the discount (used by the friendly emerald banner). We compute this
        // client-side from the existingChildren state — the backend only returns counts.
        sibling_prior_names: (() => {
          if (!siblingTierMeta?.prior_count_used) return [];
          const cartKeySet = new Set(cartChildKeys);
          return (existingChildren || [])
            .map((ec) => ({
              key: ec.id ? `id:${ec.id}` : `name:${(ec.given_name || "").trim().toLowerCase()}|${(ec.last_name || "").trim().toLowerCase()}|${(ec.birthdate || "").trim()}`,
              name: ec.given_name || ec.name || "your child",
            }))
            .filter((x) => !cartKeySet.has(x.key))
            .map((x) => x.name);
        })(),
        new_enrolled_children_count: newEnrolledChildrenCount,
        sibling_discounts_enabled: siblingDiscountsEnabled,
        sibling_discounts_available: siblingDiscountsAvailable,
        // Discount code
        discount_code: validatedDiscount?.code || null,
        discount_code_type: validatedDiscount?.discount_type || null,
        discount_code_amount: roundTo2(discountCodeAmount),
        discount_code_display: discountCodeDisplay,
        // PayNow totals (no card fee)
        total_without_dm: roundTo2(totalWithoutDM),
        total_with_dm: roundTo2(totalWithDM),
        // Card payment totals (canonical gross-up incl. Stripe's $0.50 fixed fee)
        card_fee_percent: CARD_FEE_PERCENT,
        card_fee_without_dm: cardFeeWithoutDM,
        card_fee_with_dm: cardFeeWithDM,
        total_card_without_dm: roundTo2(cardTotalWithoutDM),
        total_card_with_dm: roundTo2(cardTotalWithDM)
      });
    } catch (error) {
      console.error("Error calculating fees:", error);
      toast.error("Failed to calculate fees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step === 3 && Object.keys(childSessionSelections).length > 0 && rateTable) {
      calculateFees();
      // Reset includeDM if DM is not enabled in rate table OR the parent is
      // already an SCC member (any tier — section/club/full — they already
      // get the 10% member discount, so DM would double-dip) OR they already
      // have an active Digital Membership from a prior term.
      const dmExpIso = loggedInUser?.digital_membership_expires_at;
      const activeDM = !!(loggedInUser?.is_digital_member && (!dmExpIso || new Date(dmExpIso) > new Date()));
      const isAnySccMember = isSccMember
        || loggedInUser?.is_scc_member
        || loggedInUser?.member_type === 'section'
        || linkedMemberType === 'section'
        || linkedMemberType === 'club_member';
      if (!rateTable?.digital_membership_config?.enabled || isAnySccMember || activeDM) {
        setIncludeDM(false);
      }
    }
  }, [step, includeDM, skipRegistrationFee, selectedOptionalItems, childSessionSelections, rateTable, linkedMemberType, isSccMember, validatedDiscount, enrollableChildren]);

  const addChild = () => {
    // Pre-fill emergency contact with parent info
    const parentFullName = parentGivenName && parentLastName 
      ? `${parentGivenName} ${parentLastName}`.trim()
      : getParentFullName();
    const parentContactPhone = parentPhone || propUser?.phone || "";
    
    setChildren([
      ...children,
      createInitialChild(parentFullName, parentContactPhone)
    ]);
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      const updated = children.filter((_, i) => i !== index);
      setChildren(updated);
      // Remove session selections for removed child
      const newSelections = { ...childSessionSelections };
      delete newSelections[index];
      // Re-index remaining selections
      const reindexed = {};
      Object.keys(newSelections).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum > index) {
          reindexed[keyNum - 1] = newSelections[key];
        } else {
          reindexed[key] = newSelections[key];
        }
      });
      setChildSessionSelections(reindexed);
    }
  };

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    setChildren(updated);
  };

  const toggleSessionForChild = (childIndex, sessionId) => {
    // Find the session being toggled
    const targetSession = sessions.find(s => s.id === sessionId);
    if (!targetSession) return;
    
    const currentSessions = childSessionSelections[childIndex] || [];
    const isSelected = currentSessions.includes(sessionId);
    
    // If selecting (not deselecting), check for conflicts + weekly cap
    if (!isSelected) {
      // Universal cap: max distinct days per week (from rate table).
      // Runs before the same-day-time conflict check so the parent sees a
      // clearer message when they exceed the allowed weekly days.
      const cap = parseInt(rateTable?.max_sessions_per_week_cap) || 0;
      if (cap > 0) {
        const nextSessions = [...currentSessions, sessionId];
        const distinctDays = new Set(
          nextSessions
            .map(id => sessions.find(s => s.id === id)?.day_of_week)
            .filter(d => d !== undefined && d !== null)
        );
        if (distinctDays.size > cap) {
          toast.error(`Max ${cap} day${cap === 1 ? "" : "s"} per week allowed. Deselect another day first.`);
          return;
        }
      }
      // Check for conflicting sessions (same day + overlapping time)
      const conflictingSession = currentSessions.find(existingId => {
        const existingSession = sessions.find(s => s.id === existingId);
        if (!existingSession) return false;
        
        // Check if same day
        if (existingSession.day_of_week !== targetSession.day_of_week) return false;
        
        // Check if times overlap
        const existingStart = existingSession.start_time;
        const existingEnd = existingSession.end_time;
        const targetStart = targetSession.start_time;
        const targetEnd = targetSession.end_time;
        
        // Simple overlap check - if sessions are on same day, any overlap is a conflict
        // (Most sessions at same time on same day would conflict)
        if (existingStart === targetStart || 
            (existingStart < targetEnd && existingEnd > targetStart)) {
          return true;
        }
        return false;
      });
      
      if (conflictingSession) {
        const existingSession = sessions.find(s => s.id === conflictingSession);
        toast.error(
          `Cannot select this session - it conflicts with ${getDayName(existingSession.day_of_week)} ${existingSession.start_time} - ${existingSession.end_time} already selected.`,
          { duration: 5000 }
        );
        return;
      }
    }
    
    setChildSessionSelections(prev => ({
      ...prev,
      [childIndex]: isSelected
        ? currentSessions.filter(id => id !== sessionId)
        : [...currentSessions, sessionId]
    }));
  };

  // Validate discount code
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError("Please enter a discount code");
      return;
    }
    
    setDiscountLoading(true);
    setDiscountError("");
    
    try {
      const response = await axios.post(
        `${API}/academy/discount-codes/validate`,
        null,
        { 
          params: { code: discountCode, term_id: selectedTerm?.id || "" },
          withCredentials: true 
        }
      );
      
      if (response.data.valid) {
        setValidatedDiscount(response.data);
        toast.success(`Discount applied: ${response.data.display}`);
      } else {
        setDiscountError(response.data.error || "Invalid discount code");
        setValidatedDiscount(null);
      }
    } catch (error) {
      console.error("Error validating discount code:", error);
      setDiscountError(error.response?.data?.detail || "Failed to validate code");
      setValidatedDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscountCode = () => {
    setDiscountCode("");
    setValidatedDiscount(null);
    setDiscountError("");
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Prepare child session mapping - ONLY include children with sessions selected
      // AND who the parent actually wants enrolled this term (existing kids may be
      // unticked via the `include_this_term=false` toggle).
      // NOTE: we .map first (keeping original index so childSessionSelections lookup
      // is correct) then filter, so indices remain aligned.
      const childrenWithSessions = children
        .map((child, index) => ({
          ...child,
          session_ids: childSessionSelections[index] || []
        }))
        .filter((child) => !child.is_existing_child || child.include_this_term !== false)
        .filter(child => child.session_ids.length > 0); // Only enrolled children

      let parentId;
      let childrenIds = [];
      let registrationId = null;

      if (isExistingMember && loggedInUser) {
        // Existing member - add children to their account
        const payload = {
          existing_user_id: loggedInUser.id,
          children: childrenWithSessions,
          term_id: selectedTerm?.id,
          purchase_digital_membership: includeDM,
          digital_membership_price: includeDM ? feeCalculation?.dm_price : 0,
          skip_registration_fee: skipRegistrationFee,
          optional_items_purchased: selectedOptionalItems.map(itemId => {
            const item = optionalItems.find(i => i.id === itemId);
            return {
              item_id: itemId,
              name: item?.name,
              price: item?.base_price
            };
          }),
          kit_selections: kitSelections,
          total_amount: (includeDM ? feeCalculation?.total_with_dm : feeCalculation?.total_without_dm) + (kitExtrasTotal || 0)
        };

        const registerResponse = await axios.post(`${API}/academy/add-children-to-member`, payload, { withCredentials: true });
        parentId = loggedInUser.id;
        childrenIds = registerResponse.data.children_ids || [];
        registrationId = registerResponse.data.registration_id || null;
      } else {
        // New user - create parent account
        // Check if this is a linked registration (account already exists from section member registration)
        const linkedRegistrationId = sessionStorage.getItem('current_linked_registration_id');
        
        const payload = {
          parent_given_name: parentGivenName,
          parent_last_name: parentLastName,
          parent_email: parentEmail,
          parent_phone: parentPhone,
          parent_password: hasLinkedRegistration ? null : parentPassword, // No password for linked registrations
          residential_address: parentAddress,
          parent_nationality: parentNationality,
          is_scc_member: isSccMember,
          membership_number: isSccMember ? membershipNumber : null,
          primary_member_given_name: isSccMember ? primaryMemberGivenName : null,
          primary_member_last_name: isSccMember ? primaryMemberLastName : null,
          relationship: isSccMember ? relationshipToPrimary : null,
          children: childrenWithSessions,
          purchase_digital_membership: includeDM,
          digital_membership_price: includeDM ? feeCalculation?.dm_price : 0,
          skip_registration_fee: skipRegistrationFee,
          optional_items_purchased: selectedOptionalItems.map(itemId => {
            const item = optionalItems.find(i => i.id === itemId);
            return {
              item_id: itemId,
              name: item?.name,
              price: item?.base_price
            };
          }),
          kit_selections: kitSelections,
          total_amount: (includeDM ? feeCalculation?.total_with_dm : feeCalculation?.total_without_dm) + (kitExtrasTotal || 0),
          linked_registration_id: linkedRegistrationId || null, // Link to existing section member registration
          is_linked_registration: hasLinkedRegistration
        };

        const registerResponse = await axios.post(`${API}/academy/register-parent`, payload);
        parentId = registerResponse.data.parent_id;
        childrenIds = registerResponse.data.children_ids || [];
        registrationId = registerResponse.data.registration_id || null;
        
        // Clear linked registration ID after successful submission
        if (linkedRegistrationId) {
          sessionStorage.removeItem('current_linked_registration_id');
        }

        // Delete draft after successful registration (silent, non-blocking)
        if (draftId) {
          axios.delete(`${API}/academy/draft-registration/${draftId}`, { withCredentials: true })
            .catch(err => console.warn("Could not delete draft after submission:", err?.response?.status));
        }
      }

      // Calculate total based on payment method
      const baseTotal = (includeDM ? feeCalculation?.total_with_dm : feeCalculation?.total_without_dm) + (kitExtrasTotal || 0);
      const cardFee = includeDM ? feeCalculation?.card_fee_with_dm : feeCalculation?.card_fee_without_dm;

      if (paymentMethod === "card") {
        const checkoutPayload = {
          payment_type: "academy_registration",
          currency: "sgd",
          origin_url: window.location.origin,
          parent_id: parentId,
          registration_id: registrationId,
          children_ids: childrenIds,
          term_id: selectedTerm?.id,
          session_ids: allSelectedSessions,
          include_digital_membership: includeDM,
          parent_email: parentEmail,
          parent_name: `${parentGivenName} ${parentLastName}`,
          fee_breakdown: {
            base_fee: feeCalculation?.base_fee,
            registration_fee: feeCalculation?.registration_fee || 0,
            dm_price: includeDM ? (feeCalculation?.dm_price || 0) : 0,
            sibling_discount: feeCalculation?.sibling_discount || 0,
            member_discount: feeCalculation?.member_discount_total || 0,
            member_discount_pct: feeCalculation?.member_discount_percent || 0,
            savings_this_term: includeDM ? (feeCalculation?.savings_this_term || 0) : 0,
            card_fee: cardFee,
            // FIXED: Use child_breakdowns length for accurate enrolled children count
            children_count: feeCalculation?.child_breakdowns?.length || 0,
            term_weeks: feeCalculation?.term_weeks,
            // Enhanced details for admin visibility.
            // For brand-new registrations the parent isn't logged in yet, so we
            // also honour the wizard's local `isSccMember` state (set when the
            // user ticks the SCC member box and verifies their membership
            // number). Without this, transactions for new SCC members were
            // being saved as `is_member=false` even though the discount was
            // applied — confusing for admin verification later.
            member_status: {
              is_member: loggedInUser?.is_scc_member || loggedInUser?.member_type === 'section' || isSccMember || false,
              member_type: loggedInUser?.member_type || linkedMemberType || (isSccMember ? 'scc_member' : 'non_member'),
              membership_number: loggedInUser?.membership_number || membershipNumber || ''
            },
            term_name: selectedTerm?.name || '',
            child_details: feeCalculation?.child_breakdowns?.map((cb, idx) => ({
              name: cb.name,
              session_count: cb.sessionCount,
              sessions: cb.sessions?.map(s => ({
                day: s.day,
                time: s.time
              })) || [],
              weekly_fee: cb.weeklyFee,
              // GROSS term fee (before sibling & member) so the invoice can
              // itemise sibling and member discounts as separate lines and
              // still reconcile to the charged amount.
              term_fee: Math.round(((cb.termFee || 0) + (cb.siblingDiscount || 0)) * 100) / 100,
              member_discount: cb.memberDiscount,
              sibling_discount: cb.siblingDiscount || 0
            })) || []
          }
        };

        const checkoutResponse = await axios.post(`${API}/payments/checkout/academy-registration`, checkoutPayload);
        
        if (checkoutResponse.data.url) {
          toast.success(`Payment Reference: ${checkoutResponse.data.payment_reference}. Redirecting to payment...`);
          window.location.href = checkoutResponse.data.url;
        } else {
          throw new Error("Failed to create checkout session");
        }
      } else {
        // PAYNOW PAYMENT
        const paynowPayload = {
          payment_type: "academy_registration",
          amount: baseTotal,
          currency: "sgd",
          parent_id: parentId,
          parent_email: parentEmail,
          parent_name: `${parentGivenName} ${parentLastName}`,
          term_id: selectedTerm?.id,
          // Without this the invoice has no link back to the registration, so
          // the de-duplication cannot see the registration's own placeholder.
          registration_id: registrationId,
          children_ids: childrenIds,
          session_ids: allSelectedSessions,
          include_digital_membership: includeDM,
          fee_breakdown: {
            base_fee: feeCalculation?.base_fee,
            registration_fee: feeCalculation?.registration_fee || 0,
            dm_price: includeDM ? (feeCalculation?.dm_price || 0) : 0,
            sibling_discount: feeCalculation?.sibling_discount || 0,
            member_discount: feeCalculation?.member_discount_total || 0,
            member_discount_pct: feeCalculation?.member_discount_percent || 0,
            savings_this_term: includeDM ? (feeCalculation?.savings_this_term || 0) : 0,
            // FIXED: Use child_breakdowns length for accurate enrolled children count
            children_count: feeCalculation?.child_breakdowns?.length || 0,
            term_weeks: feeCalculation?.term_weeks,
            // Enhanced details for admin visibility.
            // For brand-new registrations the parent isn't logged in yet, so we
            // also honour the wizard's local `isSccMember` state (set when the
            // user ticks the SCC member box and verifies their membership
            // number). Without this, transactions for new SCC members were
            // being saved as `is_member=false` even though the discount was
            // applied — confusing for admin verification later.
            member_status: {
              is_member: loggedInUser?.is_scc_member || loggedInUser?.member_type === 'section' || isSccMember || false,
              member_type: loggedInUser?.member_type || linkedMemberType || (isSccMember ? 'scc_member' : 'non_member'),
              membership_number: loggedInUser?.membership_number || membershipNumber || ''
            },
            term_name: selectedTerm?.name || '',
            child_details: feeCalculation?.child_breakdowns?.map((cb, idx) => ({
              name: cb.name,
              session_count: cb.sessionCount,
              sessions: cb.sessions?.map(s => ({
                day: s.day,
                time: s.time
              })) || [],
              weekly_fee: cb.weeklyFee,
              // GROSS term fee (before sibling & member) so the invoice can
              // itemise sibling and member discounts as separate lines and
              // still reconcile to the charged amount.
              term_fee: Math.round(((cb.termFee || 0) + (cb.siblingDiscount || 0)) * 100) / 100,
              member_discount: cb.memberDiscount,
              sibling_discount: cb.siblingDiscount || 0
            })) || []
          }
        };

        const paynowResponse = await axios.post(`${API}/payments/checkout/paynow`, paynowPayload);
        
        const { transaction_id, payment_reference, amount } = paynowResponse.data;
        toast.success(`Payment Reference: ${payment_reference}. Redirecting to PayNow...`);
        navigate(`/academy/paynow-payment?ref=${payment_reference}&amount=${amount}&txn=${transaction_id}`);
      }
    } catch (error) {
      console.error("Error submitting registration:", error);
      const detail = error.response?.data?.detail;
      // Duplicate-child warn-and-confirm: show the modal, don't toast a raw error.
      if (error.response?.status === 409 && detail && detail.code === "duplicate_child") {
        setDuplicateWarning(detail.duplicates || []);
        setLoading(false);
        return;
      }
      // The child or the parent is already in the academy under another login —
      // they must sign in rather than start a second account.
      if (error.response?.status === 409 && detail
          && ["child_registered_elsewhere", "parent_exists"].includes(detail.code)) {
        setExistingAccount(detail);
        setLoading(false);
        return;
      }
      const errorMessage = (typeof detail === "string" ? detail : detail?.message) || "Registration failed";
      toast.error(errorMessage);
      
      // If email already exists, go back to step 1
      if (typeof errorMessage === "string" && errorMessage.toLowerCase().includes("email")) {
        setEmailError(errorMessage);
        setStep(1);
      }
    } finally {
      setLoading(false);
    }
  };

  // Match a wizard child row to a duplicate-warning entry (name + DOB).
  const _dupNorm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();
  const _childMatchesDup = (c, d) =>
    _dupNorm(`${c.given_name} ${c.last_name}`) === _dupNorm(`${d.given_name} ${d.last_name}`) &&
    String(c.birthdate || "").slice(0, 10) === String(d.birthdate || "").slice(0, 10);

  // "Yes, use existing child" → attach this term registration to the existing
  // academy_players record (re-enrolment); never creates a new record.
  const handleDuplicateUseExisting = () => {
    const dups = duplicateWarning || [];
    setChildren((prev) =>
      prev.map((c) => {
        const match = dups.find((d) => _childMatchesDup(c, d));
        if (!match) return c;
        return {
          ...c,
          existing_child_id: match.existing_child_id,
          is_existing_child: true,
          confirm_new_child: false,
        };
      })
    );
    setDuplicateWarning(null);
    setTimeout(() => handleSubmit(), 0);
  };

  // "No, this is a different child" → proceed to create a new record.
  const handleDuplicateTreatAsDifferent = () => {
    const dups = duplicateWarning || [];
    setChildren((prev) =>
      prev.map((c) => {
        const match = dups.find((d) => _childMatchesDup(c, d));
        if (!match) return c;
        return { ...c, confirm_new_child: true, existing_child_id: undefined };
      })
    );
    setDuplicateWarning(null);
    setTimeout(() => handleSubmit(), 0);
  };

  const nextStep = async () => {
    // Step 1: Combined Parent + Children validation
    if (step === 1) {
      // Parent validation (skip for existing members)
      if (!isExistingMember) {
        if (hasLinkedRegistration) {
          if (!parentGivenName || !parentLastName || !parentEmail || !parentPhone) {
            toast.error("Please fill in all parent information");
            return;
          }
        } else {
          if (!parentGivenName || !parentLastName || !parentEmail || !parentPhone || !parentPassword) {
            toast.error("Please fill in all required parent fields");
            return;
          }
          if (emailError) {
            toast.error("Please use a different email address");
            return;
          }
          try {
            const response = await axios.post(`${API}/academy/check-email`, { email: parentEmail });
            if (!response.data.available) {
              setEmailError("This email is already registered. Please use a different email or login.");
              toast.error("Email already registered");
              return;
            }
          } catch (error) {
            console.error("Error validating email:", error);
          }
        }
      }
      
      // Children validation - simplified: only name, birthdate, gender, medical required
      if (!children || children.length === 0) {
        toast.error("Please add at least one child to continue");
        return;
      }
      if (children.some(c => !c.given_name || !c.last_name || !c.birthdate)) {
        toast.error("Please enter name and birthdate for all children");
        return;
      }
      
      // Validate membership number if claimed
      if (isSccMember && membershipNumber && membershipValid === false) {
        toast.error("The membership number you entered could not be verified. Please correct it or uncheck the member option.");
        return;
      }
    }
    
    // Step 2: Session selection validation (was Step 3)
    if (step === 2) {
      if (!selectedTerm) {
        toast.error("Please select a term");
        return;
      }
      
      let termWeeks = selectedTerm?.duration_unit === "weeks" 
        ? (selectedTerm?.duration_value || 10)
        : Math.ceil((selectedTerm?.duration_value || 70) / 7);
      
      if (selectedTerm?.start_date) {
        const termStart = new Date(selectedTerm.start_date);
        const today = new Date();
        const daysSinceStart = Math.floor((today - termStart) / (1000 * 60 * 60 * 24));
        
        if (daysSinceStart > 0) {
          const weeksElapsed = Math.floor(daysSinceStart / 7);
          const remainingWeeks = Math.max(0, termWeeks - weeksElapsed);
          
          if (remainingWeeks <= 0) {
            toast.error("This term has already ended. Please select a different term.");
            return;
          }
          
          termWeeks = remainingWeeks;
        }
      }
      
      const minSessionsRequired = termWeeks;
      const childrenWithInsufficientSessions = [];
      let totalSessionsAllChildren = 0;
      
      children.forEach((child, childIndex) => {
        const selectedSessions = childSessionSelections[childIndex] || [];
        const sessionsPerWeek = selectedSessions.length;
        const totalSessionsForChild = sessionsPerWeek * termWeeks;
        
        if (sessionsPerWeek > 0 && totalSessionsForChild < minSessionsRequired) {
          childrenWithInsufficientSessions.push({
            name: child.given_name || `Child ${childIndex + 1}`,
            sessionsPerWeek,
            totalSessions: totalSessionsForChild,
            needed: minSessionsRequired
          });
        }
        
        totalSessionsAllChildren += totalSessionsForChild;
      });
      
      if (childrenWithInsufficientSessions.length > 0) {
        const example = childrenWithInsufficientSessions[0];
        toast.error(
          `Term enrollment requires at least 1 session per week for the full ${termWeeks}-week term. ` +
          `${example.name} has ${example.sessionsPerWeek} session(s)/week × ${termWeeks} weeks = ${example.totalSessions} total sessions. ` +
          `Minimum required: ${minSessionsRequired} sessions.`,
          { duration: 8000 }
        );
        return;
      }
      
      const enrolledCount = enrollableChildren.length;
      if (enrolledCount === 0) {
        toast.error("Please select at least one session for at least one child");
        return;
      }
      
      const skippedChildren = childrenEnrollmentStatus.filter(c => !c.isEnrollable);
      if (skippedChildren.length > 0) {
        const skippedNames = skippedChildren
          .map(c => c.child.given_name || `Child ${c.index + 1}`)
          .join(", ");
        toast.info(`Note: ${skippedNames} will not be enrolled (no sessions selected)`, {
          duration: 5000
        });
      }
    }
    
    setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const getDayName = (dayNumber) => {
    const days = ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    return days[dayNumber] || `Day ${dayNumber}`;
  };

  // Show loading while checking auth status
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0f1a] to-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0f1a] to-[#0a0f1a] p-4 md:p-8">
      {existingAccount && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="existing-account-modal"
        >
          <div className="bg-slate-900 border border-amber-500/40 rounded-xl max-w-lg w-full p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-2">
              You already have an academy account
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              {existingAccount.message}
            </p>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4 text-xs text-amber-200 leading-snug">
              Signing up again here would give your child a second record and a second membership
              number, which makes a mess of their history. Log in instead and add the new term from
              your dashboard.
            </div>
            <div className="flex gap-2 flex-col sm:flex-row">
              <button
                type="button"
                onClick={() => navigate("/")}
                data-testid="existing-account-login-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors"
              >
                Take me to log in
              </button>
              <button
                type="button"
                onClick={() => setExistingAccount(null)}
                data-testid="existing-account-dismiss-btn"
                className="flex-1 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-300 text-sm px-4 py-2.5 rounded-lg transition-colors"
              >
                Back to the form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume-existing-registration modal (Feb 2026 duplicate-guard).
          Fired when the parent picks a term for which they already have a
          live pending registration. */}
      {duplicateWarning && duplicateWarning.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="duplicate-child-modal"
        >
          <div className="bg-slate-900 border border-amber-500/40 rounded-xl max-w-lg w-full p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-2">
              {duplicateWarning.length > 1 ? "These children are" : `${duplicateWarning[0].name} is`} already registered
            </h2>
            <div className="space-y-2 mb-4">
              {duplicateWarning.map((d) => (
                <p key={d.existing_child_id} className="text-slate-300 text-sm leading-relaxed">
                  <strong className="text-white">{d.name}</strong>
                  {d.birthdate && <> (born {d.birthdate})</>} is already registered. Continue with the existing record?
                </p>
              ))}
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4 text-xs text-amber-200 leading-snug">
              Using the existing record enrols {duplicateWarning.length > 1 ? "them" : "them"} for this term without creating a duplicate. Only choose &quot;different child&quot; if this is genuinely a different person who happens to share the same name and birthdate.
            </div>
            <div className="flex gap-2 flex-col sm:flex-row">
              <button
                type="button"
                onClick={handleDuplicateUseExisting}
                data-testid="duplicate-use-existing-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors"
              >
                Yes, use existing child
              </button>
              <button
                type="button"
                onClick={handleDuplicateTreatAsDifferent}
                data-testid="duplicate-different-child-btn"
                className="flex-1 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-300 text-sm px-4 py-2.5 rounded-lg transition-colors"
              >
                No, this is a different child
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingResumeReg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="resume-registration-modal"
        >
          <div className="bg-slate-900 border border-amber-500/40 rounded-xl max-w-lg w-full p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-2">
              Continue your registration
            </h2>
            <p className="text-slate-300 text-sm mb-4 leading-relaxed">
              You already started a registration for{" "}
              <strong className="text-white">{pendingResumeReg.term_name || "this term"}</strong>
              {pendingResumeReg.children_summary?.length > 0 && (
                <>
                  {" "}for{" "}
                  <strong className="text-white">
                    {pendingResumeReg.children_summary.map(c => c.child_name).filter(Boolean).join(", ") || "your child(ren)"}
                  </strong>
                </>
              )}. Your outstanding balance is{" "}
              <strong className="text-amber-300">
                ${Number(pendingResumeReg.total_amount || 0).toFixed(2)}
              </strong>
              {pendingResumeReg.payment_reference && (
                <>
                  {" "}(invoice{" "}
                  <span className="font-mono text-xs bg-slate-800 px-1.5 py-0.5 rounded">
                    {pendingResumeReg.payment_reference}
                  </span>).
                </>
              )}
            </p>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4 text-xs text-amber-200 leading-snug">
              Rather than create a second invoice, we can update your existing
              one. If you&apos;d prefer to throw it away and start over, we&apos;ll cancel
              the old one first so you never get two invoices for the same child.
            </div>
            <div className="flex gap-2 flex-col sm:flex-row">
              <button
                type="button"
                onClick={handleResumeContinue}
                disabled={supersedingPending}
                data-testid="resume-registration-continue-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-medium px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                Continue where I left off
              </button>
              <button
                type="button"
                onClick={handleResumeStartFresh}
                disabled={supersedingPending}
                data-testid="resume-registration-startfresh-btn"
                className="flex-1 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 font-medium px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {supersedingPending ? "Cancelling…" : "Cancel that & start fresh"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Academy Registration
          </h1>
          {isExistingMember ? (
            <div>
              <p className="text-emerald-400 font-medium mb-1">
                Welcome back, {loggedInUser?.given_name || loggedInUser?.name}!
              </p>
              <p className="text-slate-400">Step {step} of 3</p>
            </div>
          ) : (
            <p className="text-slate-400">Step {step} of 3</p>
          )}
        </div>

        {/* Back to Dashboard for logged-in users */}
        {isExistingMember && (
          <div className="mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="text-blue-400"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        )}

        {/* Progress Bar - 3 steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {(isExistingMember ? [1, 2, 3] : [1, 2, 3]).map((s, index) => {
              const stepLabels = isExistingMember
                ? ["Your Children", "Sessions", "Review & Pay"]
                : ["Your Details", "Sessions", "Review & Pay"];
              return (
                <div key={s} className="flex flex-col items-center flex-1">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    s <= step ? "bg-blue-600 text-white" : "bg-slate-700 text-slate-400"
                  } font-semibold`}>
                    {s < step ? <Check className="w-5 h-5" /> : index + 1}
                  </div>
                  <span className={`text-xs mt-1 ${s <= step ? "text-blue-400" : "text-slate-500"}`}>{stepLabels[index]}</span>
                </div>
              );
            })}
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${((step - (isExistingMember ? 1 : 0)) / (isExistingMember ? 2 : 3)) * 100}%` }}
            />
          </div>
        </div>

        {/* Draft Status Indicator - shown for non-logged-in users once a draft exists */}
        {!isExistingMember && hasDraft && (
          <div className="mb-4 flex items-center justify-between gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="draft-status-banner">
            <div className="flex items-center gap-2 text-sm text-emerald-300">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span>
                Progress auto-saved to <strong>{parentEmail}</strong>
                {lastSaved && ` · ${new Date(lastSaved).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
              </span>
            </div>
            <Button variant="ghost" size="sm"
              onClick={() => { saveDraft(true); toast.info("Draft saved. You can close this page and resume later."); }}
              disabled={savingDraft}
              className="text-emerald-300 hover:text-emerald-200 text-xs h-7"
              data-testid="finish-later-btn">
              {savingDraft ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Finish later"}
            </Button>
          </div>
        )}

        {/* Step Content */}
        <Card className="p-6 md:p-8">
          {/* Step 1: Parent + Children Info (COMBINED & SIMPLIFIED) */}
          {step === 1 && !isExistingMember && (
            <div className="space-y-6">
              {/* What You Get Banner */}
              <Card className="p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30">
                <h3 className="font-bold text-blue-300 mb-2">What your child gets with SCC Football Academy:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-400">
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-400" /><span>Unique academy membership number to track all activities</span></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-400" /><span>Parent portal — payments, medical leave, progress tracking</span></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-400" /><span>Child login to view team progress (parent-controlled)</span></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-400" /><span>Game & training scheduling with opt-in/out</span></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-400" /><span>Telegram notifications for schedule changes</span></div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-400" /><span>Club & sibling discounts applied automatically</span></div>
                </div>
              </Card>

              <h2 className="text-2xl font-bold">Your Details</h2>
              
              {/* Resume Draft Link */}
              {!hasDraft && (
                <div className="bg-blue-500/10 p-3 rounded-lg border border-blue-500/30 text-sm">
                  <p className="text-blue-400">
                    Already started a registration?{" "}
                    <button type="button" onClick={sendDraftReminder} className="text-blue-400 underline font-medium hover:text-blue-400">
                      Send me a link to resume
                    </button>
                  </p>
                </div>
              )}

              {/* Soft duplicate-account warning (phone / name / child match) */}
              {duplicateMatches.length > 0 && !duplicateDismissed && (
                <div className="bg-amber-500/10 p-4 rounded-lg border border-amber-500/40" data-testid="duplicate-warning-banner">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-300 mb-1">Looks like you might already have an account</p>
                      <p className="text-xs text-amber-200/80 mb-3">
                        We found {duplicateMatches.length === 1 ? "a match" : `${duplicateMatches.length} matches`} using the info you entered. If any of these is you, please login instead of creating a new account:
                      </p>
                      <ul className="space-y-1.5 mb-3">
                        {duplicateMatches.slice(0, 3).map((m, idx) => (
                          <li key={idx} className="text-sm text-amber-100">
                            <span className="font-mono text-amber-200">{m.masked_email}</span>
                            <span className="text-xs text-amber-200/60 ml-2">
                              · matched on {m.reason === "phone" ? "phone number" : m.reason === "parent_name" ? "your name" : `child (${m.reason.replace("child:", "")})`}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => navigate("/?login=true")} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="duplicate-login-btn">
                          Go to Login
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDuplicateDismissed(true)} className="text-amber-300 hover:text-amber-200" data-testid="duplicate-dismiss-btn">
                          This isn't me — continue
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Parent Section */}
              <Card className="p-4 bg-[#0a0f1a]">
                <h3 className="font-semibold mb-3 text-slate-300">Parent / Guardian</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Given Name *</label>
                    <Input value={parentGivenName} onChange={(e) => setParentGivenName(e.target.value)} placeholder="John" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Last Name *</label>
                    <Input value={parentLastName} onChange={(e) => setParentLastName(e.target.value)} placeholder="Doe" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <Input type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} placeholder="parent@example.com"
                      className={emailError ? "border-red-500" : hasLinkedRegistration ? "bg-slate-800/50" : ""}
                      disabled={hasLinkedRegistration} />
                    {hasLinkedRegistration && <p className="text-xs text-emerald-400 mt-1">Account already created with this email</p>}
                    {emailError && !hasLinkedRegistration && (
                      <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <div className="flex items-start gap-2 text-red-400 text-sm">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <span>{emailError}</span>
                            {emailAction && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                <Button type="button" size="sm" onClick={() => navigate(`/?login=true&email=${encodeURIComponent(parentEmail)}`)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white">
                                  {emailAction === "login_and_pay" ? "Login to Complete Payment" : "Login to Your Account"}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone *</label>
                    <Input type="tel" value={parentPhone} onChange={(e) => setParentPhone(e.target.value)} placeholder="+65 9123 4567" />
                  </div>
                </div>
                
                {/* Password - only for new accounts */}
                {!hasLinkedRegistration && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1">Create Password *</label>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} value={parentPassword}
                        onChange={(e) => setParentPassword(e.target.value)} placeholder="Create a secure password" className="pr-10" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300">
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">You&apos;ll use this to login to the portal</p>
                  </div>
                )}

                {/* SCC Membership Check with real-time validation */}
                <div className="mt-4 p-3 rounded-lg border border-slate-700 bg-slate-800/30">
                  <div className="flex items-center space-x-3">
                    <Checkbox id="scc-member-check" checked={isSccMember}
                      onCheckedChange={(checked) => {
                        setIsSccMember(checked);
                        if (!checked) { setMembershipNumber(""); setMembershipValid(null); }
                      }} />
                    <label htmlFor="scc-member-check" className="text-sm font-medium cursor-pointer">
                      I am an SCC Club Member / Football Section Member
                    </label>
                  </div>
                  <details className="mt-2 ml-7 group" data-testid="scc-definition-help">
                    <summary className="text-xs text-blue-300/80 cursor-pointer hover:text-blue-300 select-none list-none">
                      Who counts as a member? <span className="underline">Learn more</span>
                    </summary>
                    <div className="mt-2 text-xs text-slate-300/90 space-y-1.5 border-l-2 border-slate-700 pl-3">
                      <p><strong className="text-blue-300">SCC Club Member</strong> — you hold a Full Transferable,
                        Term or Sports Membership of the Singapore Cricket Club, pay a monthly subscription to the Club
                        and have been issued an SCC membership card. Gets 10% off term fees once verified by admin.</p>
                      <p><strong className="text-teal-300">Digital Member</strong> — you purchased an Academy Digital Membership.
                        Gets the same 10% off, applied straight away, as long as it's valid at the term's start date.</p>
                      <p><strong className="text-slate-400">Non-member</strong> — neither of the above. Pays full fees.</p>
                      <p className="text-slate-400 italic">Enter the membership number exactly as it appears on your card.
                        An admin confirms it against the Club's records before the discount is finalised.</p>
                    </div>
                  </details>
                  {isSccMember && loggedInUser?.scc_member_verified && (
                    <div className="mt-3 ml-7 p-3 rounded-md border border-emerald-500/40 bg-emerald-500/10" data-testid="scc-member-verified-banner">
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 text-sm">
                          <p className="text-emerald-300 font-medium">Verified SCC Member</p>
                          <p className="text-xs text-emerald-200/80 mt-0.5">
                            Membership <span className="font-mono">{loggedInUser.membership_number || "on file"}</span>
                            {loggedInUser.member_family_relationship && ` · ${loggedInUser.member_family_relationship}`}
                            {loggedInUser.member_primary_member_name && ` of ${loggedInUser.member_primary_member_name}`}
                            . Member rates apply — no need to re-verify.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {isSccMember && !loggedInUser?.scc_member_verified && (
                    <div className="mt-3 ml-7 space-y-3">
                      <div className="p-3 rounded-md border border-amber-500/30 bg-amber-500/5" data-testid="scc-unverified-guidance">
                        <p className="text-xs text-amber-200/90 leading-relaxed">
                          <strong className="text-amber-300">Heads up:</strong> your membership hasn't been
                          confirmed by our admin yet. You'll be charged the <strong>full fee</strong> for now — please
                          complete payment and contact our admin at <span className="font-mono">academy@sccfootball.com</span> to
                          verify your membership. Once verified, any <strong>10% difference is refunded or credited</strong> to
                          your account.
                        </p>
                        <p className="text-xs text-teal-200/90 leading-relaxed mt-2">
                          Prefer an <strong>instant 10%</strong>? Add a <strong>Digital Membership</strong> at the payment
                          step — it applies the member discount straight away for this term.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Membership Number *</label>
                        <div className="flex gap-2">
                          <Input value={membershipNumber}
                            onChange={(e) => { setMembershipNumber(e.target.value.toUpperCase()); setMembershipValid(null); }}
                                placeholder="e.g. Your SCC membership card number"
                            className="flex-1" data-testid="membership-number-input" />
                          <Button type="button" size="sm" disabled={!membershipNumber || membershipValidating}
                            data-testid="verify-membership-btn"
                            onClick={async () => {
                              setMembershipValidating(true);
                              try {
                                const fmtRes = await axios.post(`${API}/academy/validate-membership-format`, {
                                  membership_number: membershipNumber,
                                  last_name: parentLastName,
                                  primary_member_given_name: primaryMemberGivenName,
                                  primary_member_last_name: primaryMemberLastName,
                                  relationship: relationshipToPrimary,
                                });
                                if (!fmtRes.data.valid) {
                                  setMembershipValid(false);
                                  toast.error(fmtRes.data.error || "Membership number could not be verified.");
                                  return;
                                }
                                const parsed = fmtRes.data.parsed || {};
                                setMembershipValid(true);
                                if (parsed.requires_manual_verification) {
                                  toast.success("Details captured. An admin will verify your family membership when approving payment.");
                                } else {
                                  toast.success("Membership format valid — member rates will apply.");
                                }
                                // Existing-account duplicate hint (non-blocking)
                                try {
                                  const existRes = await axios.get(`${API}/auth/check-membership/${membershipNumber}`);
                                  if (existRes.data.exists) {
                                    toast.info(`This number may already be linked to an account (${existRes.data.masked_email}). If so, please login instead.`);
                                  }
                                } catch { /* non-blocking */ }
                              } catch (err) {
                                setMembershipValid(false);
                                toast.error(err.response?.data?.error || err.response?.data?.detail || "Unable to verify membership.");
                              } finally {
                                setMembershipValidating(false);
                              }
                            }}
                            className={membershipValid === true ? "bg-emerald-600" : "bg-blue-600 hover:bg-blue-700"}>
                            {membershipValidating ? <RefreshCw className="w-4 h-4 animate-spin" /> : membershipValid === true ? <Check className="w-4 h-4" /> : "Verify"}
                          </Button>
                        </div>

                        {/* Family-suffix primary-member block (HFM/WFM/FFM/MFM) */}
                        {/WFM$|HFM$|FFM$|MFM$/i.test(membershipNumber) && (
                          <div className="mt-3 p-3 rounded-md border border-amber-500/30 bg-amber-500/5" data-testid="primary-member-block">
                            <p className="text-xs text-amber-300 mb-2">
                              You are registering as a <strong>family member</strong>. Please provide details of the primary SCC member (the cardholder this number belongs to). Our admin will verify this before applying member rates.
                            </p>
                            <div className="grid md:grid-cols-2 gap-2">
                              <Input
                                placeholder="Primary member's given name *"
                                value={primaryMemberGivenName}
                                onChange={(e) => { setPrimaryMemberGivenName(e.target.value); setMembershipValid(null); }}
                                data-testid="primary-given-input" />
                              <Input
                                placeholder="Primary member's last name *"
                                value={primaryMemberLastName}
                                onChange={(e) => { setPrimaryMemberLastName(e.target.value); setMembershipValid(null); }}
                                data-testid="primary-last-input" />
                            </div>
                            <select
                              value={relationshipToPrimary}
                              onChange={(e) => { setRelationshipToPrimary(e.target.value); setMembershipValid(null); }}
                              className="mt-2 w-full border rounded-md px-2 py-1.5 bg-slate-800 border-slate-600 text-white text-sm"
                              data-testid="relationship-select">
                              <option value="">Your relationship to primary member *</option>
                              {/WFM$/i.test(membershipNumber) && <option value="Wife">Wife (of primary member)</option>}
                              {/HFM$/i.test(membershipNumber) && <option value="Husband">Husband (of primary member)</option>}
                              {/FFM$/i.test(membershipNumber) && <option value="Father">Father (of primary member)</option>}
                              {/MFM$/i.test(membershipNumber) && <option value="Mother">Mother (of primary member)</option>}
                            </select>
                          </div>
                        )}

                        {membershipValid === true && <p className="text-xs text-emerald-400 mt-2">Details captured — continue to the next step.</p>}
                        {membershipValid === false && <p className="text-xs text-amber-400 mt-2">Unable to verify — please correct the details above, or uncheck the SCC member box and proceed as non-member. Contact academy@sccfootball.com if you need help.</p>}
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Children Section */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Child Information</h3>
                {children.length > 1 && (
                  <Card className="bg-emerald-500/10 p-3 border-green-500/30 mb-3">
                    <div className="flex items-center gap-2 text-emerald-400 text-sm">
                      <Users className="w-4 h-4" />
                      <span><strong>Sibling discount</strong> will be applied for {children.length} children!</span>
                    </div>
                  </Card>
                )}
                
                {children.map((child, index) => (
                  <Card key={index} className="p-4 bg-[#0a0f1a] mb-3">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm">Child {index + 1}</h4>
                      {children.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeChild(index)}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10 text-xs">Remove</Button>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Given Name *</label>
                        <Input value={child.given_name} onChange={(e) => updateChild(index, "given_name", e.target.value)} placeholder="Given name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Last Name *</label>
                        <Input value={child.last_name} onChange={(e) => updateChild(index, "last_name", e.target.value)} placeholder="Last name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                        <Input type="date" value={child.birthdate} onChange={(e) => updateChild(index, "birthdate", e.target.value)} />
                        {child.birthdate && <p className="text-xs text-blue-400 mt-1">Age: {calculateAge(child.birthdate)} years</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Gender *</label>
                        <select value={child.gender} onChange={(e) => updateChild(index, "gender", e.target.value)}
                          className="w-full border rounded-md px-3 py-2 bg-slate-800 border-slate-600 text-white">
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Medical Conditions / Allergies</label>
                        <textarea value={child.medical_conditions} onChange={(e) => updateChild(index, "medical_conditions", e.target.value)}
                          placeholder="Please declare any conditions, allergies, or special needs we should know about..."
                          className="w-full border rounded-md px-3 py-2 min-h-[60px] bg-slate-800 border-slate-600 text-white placeholder:text-slate-400" />
                      </div>

                      {rateTable?.registration_fee?.enabled && rateTable?.registration_fee?.amount > 0 && (
                        <div className="md:col-span-2">
                          <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-800/50">
                            <input type="checkbox"
                              checked={child.charge_registration_fee !== false}
                              onChange={(e) => updateChild(index, "charge_registration_fee", e.target.checked)}
                              className="mt-1 w-4 h-4 accent-amber-500"
                              data-testid={`reg-fee-checkbox-${index}`} />
                            <span className="text-sm">
                              <span className="font-medium text-slate-200">
                                Charge one-time Registration Fee (${rateTable.registration_fee.amount.toFixed(2)})
                              </span>
                              <span className="block text-xs text-slate-400 mt-0.5">
                                Uncheck only if this child has already paid the registration fee in a previous term (e.g. existing academy player). Admin will verify at payment approval.
                              </span>
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              {/* Info Banner */}
              {hasLinkedRegistration ? (
                <Card className={`p-4 border-2 ${linkedMemberType === 'section' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-blue-500/10 border-blue-500/30'}`}>
                  <div className="flex items-start gap-3">
                    <Gift className={`w-5 h-5 mt-0.5 ${linkedMemberType === 'section' ? 'text-emerald-400' : 'text-blue-400'}`} />
                    <div className="text-sm">
                      <p className={`font-semibold mb-1 ${linkedMemberType === 'section' ? 'text-green-300' : 'text-blue-300'}`}>
                        {linkedMemberType === 'section' ? 'Football Section Member Discount Applied!' : 'Club Member Discount Applied!'}
                      </p>
                      <ul className={`space-y-1 ${linkedMemberType === 'section' ? 'text-emerald-400' : 'text-blue-400'}`}>
                        <li>Your membership discount will be applied automatically</li>
                        <li>Sibling discounts apply for multiple children</li>
                      </ul>
                    </div>
                  </div>
                </Card>
              ) : !isSccMember && (
                <Card className="bg-[#0a0f1a] p-4 border-blue-500/30">
                  <div className="flex items-start gap-3">
                    <Gift className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div className="text-sm text-blue-400">
                      <p className="font-semibold text-blue-300 mb-1">Open to All!</p>
                      <p>Club Members, Football Section Members, and Digital Members enjoy discounts. Sibling discounts apply for multiple children.</p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Step 1 for returning members: Children Info Only */}
          {step === 1 && isExistingMember && (
            <div className="space-y-6">
              {/* What You Get Banner - also show to returning members */}
              <Card className="p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30">
                <h3 className="font-bold text-blue-300 mb-2">Registering for a new term</h3>
                <p className="text-sm text-blue-400">
                  {children.some((c) => c.is_existing_child)
                    ? "Tick the children you want to enroll this term, then pick their sessions in the next step. You can also add a new child below."
                    : "Add your children below, then select their training sessions in the next step."}
                </p>
              </Card>

              {loggedInUser?.is_scc_member && !loggedInUser?.scc_member_verified && (
                <Card className="p-4 border border-amber-500/30 bg-amber-500/5" data-testid="scc-unverified-guidance-existing">
                  <p className="text-xs text-amber-200/90 leading-relaxed">
                    <strong className="text-amber-300">Your SCC membership isn't verified yet.</strong> Your account claims
                    SCC membership, but our admin hasn't confirmed it yet. You'll be charged the
                    <strong> full fee</strong> for now — please contact our admin at <span className="font-mono">academy@sccfootball.com</span> to
                    verify. Once verified, any <strong>10% difference is refunded or credited</strong> to your account.
                  </p>
                  <p className="text-xs text-teal-200/90 leading-relaxed mt-2">
                    Prefer an <strong>instant 10%</strong>? Add a <strong>Digital Membership</strong> at the payment step.
                  </p>
                </Card>
              )}
              {loggedInUser?.scc_member_verified && (
                <Card className="p-3 border border-emerald-500/40 bg-emerald-500/10" data-testid="scc-verified-note-existing">
                  <p className="text-xs text-emerald-200/90">
                    <Check className="w-3.5 h-3.5 inline mr-1 text-emerald-400" />
                    Verified SCC Member — member rates apply automatically.
                  </p>
                </Card>
              )}
              {children.filter((c) => !c.is_existing_child || c.include_this_term).length > 1 && (
                <Card className="bg-emerald-500/10 p-3 border-green-500/30">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm">
                    <Users className="w-4 h-4" />
                    <span><strong>Sibling discount</strong> will be applied for {children.filter((c) => !c.is_existing_child || c.include_this_term).length} children!</span>
                  </div>
                </Card>
              )}

              {children.map((child, index) => {
                // Existing child — show compact pre-filled card with tick-to-enroll
                if (child.is_existing_child) {
                  const checked = child.include_this_term !== false;
                  const displayName = `${child.given_name || ""} ${child.last_name || ""}`.trim() || "Child";
                  return (
                    <Card
                      key={index}
                      className={`p-4 border ${checked ? "bg-emerald-950/30 border-emerald-500/50" : "bg-slate-900/40 border-slate-700 opacity-70"}`}
                      data-testid={`existing-child-card-${index}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => updateChild(index, "include_this_term", e.target.checked)}
                          className="mt-1 w-5 h-5 accent-emerald-500 cursor-pointer"
                          data-testid={`enroll-existing-${index}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-white text-base">{displayName}</h4>
                            <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded">Existing academy player</span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-400 mt-1">
                            {child.birthdate && <span>DOB: {child.birthdate} {child.birthdate ? `(Age ${calculateAge(child.birthdate)})` : ""}</span>}
                            {child.gender && <span>Gender: {child.gender}</span>}
                          </div>
                          {child.medical_conditions && (
                            <p className="text-xs text-amber-300/80 mt-1">Medical: {child.medical_conditions}</p>
                          )}
                          {!checked && (
                            <p className="text-xs text-slate-500 mt-2 italic">Won't be enrolled this term.</p>
                          )}
                          {checked && (
                            <div className="mt-3 space-y-2">
                              {/* Editable medical field — admins/parents often need to update */}
                              <details className="text-xs">
                                <summary className="cursor-pointer text-slate-400 hover:text-white">Update medical info or emergency contact</summary>
                                <div className="mt-2 grid md:grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Medical / Allergies</label>
                                    <textarea
                                      value={child.medical_conditions || ""}
                                      onChange={(e) => { updateChild(index, "medical_conditions", e.target.value); updateChild(index, "medical_info", e.target.value); }}
                                      className="w-full border rounded-md px-2 py-1.5 min-h-[50px] bg-slate-800 border-slate-600 text-white text-xs"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Emergency contact</label>
                                    <Input
                                      value={child.emergency_contact || ""}
                                      onChange={(e) => updateChild(index, "emergency_contact", e.target.value)}
                                      className="bg-slate-800 border-slate-600 text-white text-xs h-8"
                                    />
                                  </div>
                                </div>
                              </details>

                              {rateTable?.registration_fee?.enabled && rateTable?.registration_fee?.amount > 0 && (
                                <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-800/40">
                                  <input
                                    type="checkbox"
                                    checked={child.charge_registration_fee === true}
                                    onChange={(e) => updateChild(index, "charge_registration_fee", e.target.checked)}
                                    className="mt-1 w-4 h-4 accent-amber-500"
                                    data-testid={`reg-fee-checkbox-existing-${index}`}
                                  />
                                  <span className="text-xs text-slate-300">
                                    Charge one-time Registration Fee (${rateTable.registration_fee.amount.toFixed(2)})
                                    <span className="block text-slate-500 mt-0.5">
                                      {existingChildren.find((ec) => ec.id === child.existing_child_id)?.registration_fee_paid
                                        ? "Already paid in an earlier term, so it is not charged again."
                                        : "No earlier term has been paid for this child, so the one-time fee applies."}
                                    </span>
                                  </span>
                                </label>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                }

                // NEW child — full form
                return (
                  <Card key={index} className="p-4 bg-[#0a0f1a]" data-testid={`new-child-card-${index}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold">New child {(children.filter((c, i) => !c.is_existing_child && i <= index).length)}</h4>
                      <Button variant="ghost" size="sm" onClick={() => removeChild(index)}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`remove-new-child-${index}`}>Remove</Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Given Name *</label>
                        <Input value={child.given_name} onChange={(e) => updateChild(index, "given_name", e.target.value)} placeholder="Given name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Last Name *</label>
                        <Input value={child.last_name} onChange={(e) => updateChild(index, "last_name", e.target.value)} placeholder="Last name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                        <Input type="date" value={child.birthdate} onChange={(e) => updateChild(index, "birthdate", e.target.value)} />
                        {child.birthdate && <p className="text-xs text-blue-400 mt-1">Age: {calculateAge(child.birthdate)} years</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Gender *</label>
                        <select value={child.gender} onChange={(e) => updateChild(index, "gender", e.target.value)}
                          className="w-full border rounded-md px-3 py-2 bg-slate-800 border-slate-600 text-white">
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Medical Conditions / Allergies</label>
                        <textarea value={child.medical_conditions} onChange={(e) => updateChild(index, "medical_conditions", e.target.value)}
                          placeholder="Please declare any conditions, allergies, or special needs..."
                          className="w-full border rounded-md px-3 py-2 min-h-[60px] bg-slate-800 border-slate-600 text-white placeholder:text-slate-400" />
                      </div>

                      {rateTable?.registration_fee?.enabled && rateTable?.registration_fee?.amount > 0 && (
                        <div className="md:col-span-2">
                          <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-800/50">
                            <input type="checkbox"
                              checked={child.charge_registration_fee !== false}
                              onChange={(e) => updateChild(index, "charge_registration_fee", e.target.checked)}
                              className="mt-1 w-4 h-4 accent-amber-500"
                              data-testid={`reg-fee-checkbox-linked-${index}`} />
                            <span className="text-sm">
                              <span className="font-medium text-slate-200">
                                Charge one-time Registration Fee (${rateTable.registration_fee.amount.toFixed(2)})
                              </span>
                              <span className="block text-xs text-slate-400 mt-0.5">
                                Uncheck only if this child has already paid the registration fee in a previous term. Admin will verify at payment approval.
                              </span>
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}

              {/* + Add new child (sibling) */}
              <Button
                variant="outline"
                onClick={addChild}
                className="w-full border-dashed border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                data-testid="add-new-sibling-btn"
              >
                + Add a new child (sibling)
              </Button>
            </div>
          )}

          {/* Step 2: Select Terms & Sessions (Per Child) — Simplified */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Select Training Sessions</h2>
                <p className="text-sm text-slate-400">
                  Choose the days you&apos;d like your child to train. Select Social or Competitive first.
                </p>
              </div>

              {/* Existing-siblings banner for returning parents — siblings DO still count for
                  sibling-discount tiers but registration fees are now per-child parent-controlled */}
              {existingChildren.length > 0 && (
                <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10" data-testid="existing-siblings-banner">
                  <p className="text-sm text-emerald-300 font-medium">
                    ✓ We see you already have {existingChildren.length} child{existingChildren.length > 1 ? "ren" : ""} at the academy ({existingChildren.map(c => c.given_name || c.name).join(", ")}).
                  </p>
                  <p className="text-xs text-emerald-200/80 mt-1">
                    Sibling discounts apply across all your children. The one-time registration fee is charged only for children who have never paid it — you can change that on each child&apos;s card in Step 1.
                  </p>
                </div>
              )}

                {/* Compact pricing hint */}
                {rateTable && (
                  <Card className="p-3 bg-slate-800/30 border-slate-700 mb-4">
                    <p className="text-xs text-slate-400">
                      {rateTable.membership_discounts?.section_member?.value > 0 && "Section members get discounts. "}
                      {rateTable.sibling_discounts?.length > 0 && "Sibling discounts apply. "}
                      Prices shown per session — final total shown in Review step.
                    </p>
                  </Card>
                )}

              {/* Term Selection — Programme Chooser */}
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label className="block text-sm font-medium">Available Terms</label>
                  <span className="text-xs text-slate-500 hidden sm:inline">
                    Tap a term to select it
                  </span>
                </div>

                {/* Filter Tabs — Programme Type */}
                {terms.length > 0 && (() => {
                  // Build set of programme types actually present in the terms list so we
                  // can grey-out tabs with zero matches (still visible for consistency).
                  const presentTypes = new Set(terms.map(t => t?.programme_type).filter(Boolean));
                  return (
                    <div
                      className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1 scrollbar-thin"
                      data-testid="programme-filter-tabs"
                    >
                      {PROGRAMME_FILTER_TABS.map((tab) => {
                        const isActive = programmeFilter === tab.key;
                        const preset = tab.key !== "all" ? PROGRAMME_TYPES[tab.key] : null;
                        const hasAny = tab.key === "all" || presentTypes.has(tab.key);
                        return (
                          <button
                            key={tab.key}
                            type="button"
                            onClick={() => setProgrammeFilter(tab.key)}
                            data-testid={`programme-filter-${tab.key}`}
                            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition whitespace-nowrap ${
                              isActive
                                ? preset
                                  ? preset.pill + " ring-2 ring-offset-0"
                                  : "bg-slate-200 text-slate-900 border border-slate-200"
                                : "bg-slate-800/60 text-slate-300 border border-slate-700 hover:bg-slate-800"
                            } ${!hasAny ? "opacity-40" : ""}`}
                          >
                            {preset?.Icon && <preset.Icon className="w-3.5 h-3.5" />}
                            {tab.label}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                <div className="space-y-3">
                  {terms.length === 0 ? (
                    <p className="text-slate-400 text-sm">No terms available for registration at this time.</p>
                  ) : (() => {
                    const filteredTerms = programmeFilter === "all"
                      ? terms
                      : terms.filter(t => t?.programme_type === programmeFilter);

                    if (filteredTerms.length === 0) {
                      return (
                        <div className="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-700 rounded-lg">
                          No terms in this category right now.
                          <button
                            type="button"
                            onClick={() => setProgrammeFilter("all")}
                            className="ml-2 text-blue-400 hover:underline"
                            data-testid="programme-filter-clear"
                          >
                            See all terms
                          </button>
                        </div>
                      );
                    }

                    return filteredTerms.map((term) => {
                      const startDate = new Date(term.start_date);
                      const endDate = new Date(term.end_date);
                      const weeksDisplay = term.training_weeks || Math.ceil((endDate - startDate) / (7 * 24 * 60 * 60 * 1000));
                      const preset = term.programme_type ? PROGRAMME_TYPES[term.programme_type] : null;
                      const isSelected = selectedTerm?.id === term.id;
                      const highlights = (Array.isArray(term.highlights) ? term.highlights : []).filter(Boolean).slice(0, 3);
                      const blurb = (term.description || "").trim() || preset?.defaultBlurb || "";

                      return (
                        <Card
                          key={term.id}
                          onClick={() => setSelectedTerm(term)}
                          data-testid={`term-card-${term.id}`}
                          className={`p-4 cursor-pointer transition border ${
                            isSelected
                              ? "border-blue-500 bg-blue-500/10 ring-1 ring-blue-500/40"
                              : preset
                                ? `${preset.accent} hover:bg-slate-800/40`
                                : "border-slate-700 hover:border-slate-500"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              {/* Pill + Name row */}
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                {preset && (
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${preset.pill}`}
                                    data-testid={`term-pill-${term.programme_type}`}
                                  >
                                    <preset.Icon className="w-3 h-3" />
                                    {preset.label}
                                  </span>
                                )}
                                <h3 className="font-semibold text-white truncate">{term.name}</h3>
                              </div>

                              {/* Dates */}
                              <p className="text-xs text-slate-400">
                                {startDate.toLocaleDateString()} – {endDate.toLocaleDateString()}
                                <span className="ml-2 font-medium text-blue-400">
                                  ({weeksDisplay} weeks)
                                </span>
                              </p>

                              {/* Description */}
                              {blurb && (
                                <p className="text-sm text-slate-300 mt-2 leading-snug">{blurb}</p>
                              )}

                              {/* Best-for line */}
                              {term.target_audience && (
                                <p className="text-xs text-slate-400 mt-2 flex items-start gap-1">
                                  <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-500" />
                                  <span><span className="text-slate-500">Best for:</span> {term.target_audience}</span>
                                </p>
                              )}

                              {/* Highlights bullets */}
                              {highlights.length > 0 && (
                                <ul className="mt-2 space-y-1">
                                  {highlights.map((hl, i) => (
                                    <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5">
                                      <Check className="w-3 h-3 mt-0.5 flex-shrink-0 text-emerald-400" />
                                      <span>{hl}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>

                            {isSelected && (
                              <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                            )}
                          </div>
                        </Card>
                      );
                    });
                  })()}

                  {/* Cross-promo tile — Camps live under a separate flow (db.events) */}
                  {programmeFilter === "all" && terms.length > 0 && (
                    <button
                      type="button"
                      onClick={() => navigate("/camps")}
                      data-testid="camps-cross-promo-tile"
                      className="w-full mt-2 flex items-center justify-between gap-3 p-4 rounded-lg border border-dashed border-slate-600 bg-slate-900/40 hover:bg-slate-800/50 transition text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-500/15 border border-orange-400/30 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-orange-300" />
                        </div>
                        <div>
                          <p className="font-semibold text-white text-sm">Also on offer: Holiday Camps</p>
                          <p className="text-xs text-slate-400">Short-format programmes during school breaks.</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    </button>
                  )}
                </div>
              </div>

              {/* Per-Child Session Selection */}
              {selectedTerm && sessions.length > 0 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-lg">Select Sessions for Each Child</h3>
                    <p className="text-sm text-slate-400 mt-1">
                      Choose the training days for each child. We&apos;ll handle the rest.
                    </p>
                    <p className="text-xs text-emerald-400 mt-1">
                      💡 Sibling discounts will only apply to children who are signed up for sessions.
                    </p>
                  </div>
                  
                  {/* IMPORTANT: Term Commitment Explanation */}
                  <Card className="p-4 bg-blue-500/10 border-blue-500/30">
                    <div className="flex items-start gap-3">
                      <Calendar className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-blue-400 text-lg">📅 Full Term Enrollment</p>
                        <p className="text-blue-300 mt-2">
                          When you select a session (e.g., <strong>Tuesday 5:30pm</strong>), your child will be enrolled for 
                          <strong className="text-white"> ALL {selectedTerm?.duration_value || 10} weeks</strong> of the term.
                        </p>
                        <div className="mt-3 p-3 bg-slate-900/50 rounded-lg">
                          <p className="text-sm text-slate-300">
                            <span className="text-yellow-400">Example:</span> Selecting 1 session/week = 
                            <strong className="text-white"> {selectedTerm?.duration_value || 10} sessions total</strong> for the term
                          </p>
                        </div>
                        <p className="text-xs text-amber-400 mt-2">
                          ⚠️ No individual session bookings. No refunds for missed sessions once enrolled.
                        </p>
                      </div>
                    </div>
                  </Card>
                  
                  {/* Session Type Filter */}
                  <Card className="p-4 bg-slate-800/50 border-slate-700">
                    <p className="font-medium text-white mb-3">What type of sessions are you interested in?</p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => setSessionTypeFilter("all")}
                        className={`px-4 py-2 rounded-lg font-medium transition ${
                          sessionTypeFilter === "all"
                            ? "bg-purple-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        🎯 All Sessions
                      </button>
                      <button
                        onClick={() => setSessionTypeFilter("social")}
                        className={`px-4 py-2 rounded-lg font-medium transition ${
                          sessionTypeFilter === "social"
                            ? "bg-green-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        🎯 Social / Recreational
                      </button>
                      <button
                        onClick={() => setSessionTypeFilter("competitive")}
                        className={`px-4 py-2 rounded-lg font-medium transition ${
                          sessionTypeFilter === "competitive"
                            ? "bg-orange-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        ⚽ Competitive
                      </button>
                    </div>
                    {sessionTypeFilter !== "all" && (
                      <p className="text-xs text-slate-400 mt-2">
                        Showing only <strong>{sessionTypeFilter === "social" ? "Social/Recreational" : "Competitive"}</strong> sessions. 
                        <button onClick={() => setSessionTypeFilter("all")} className="text-blue-400 ml-1 underline">Show all</button>
                      </p>
                    )}
                  </Card>
                  
                  {/* Sibling discount active indicator - compact inline version */}
                  {rateTable?.sibling_discounts_enabled !== false && rateTable?.sibling_discounts?.length > 0 && children.length > 1 && (
                    <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 w-fit">
                      <span>👨‍👩‍👧‍👦</span>
                      <span>
                        Sibling discount active: {rateTable.sibling_discounts.find(d => children.length >= d.child_number)?.value || 0}% off
                      </span>
                    </div>
                  )}
                  
                  {/* Summary of enrollment status */}
                  {ineligibleChildren.length > 0 && (
                    <Card className="p-3 bg-orange-500/10 border-orange-500/30">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-orange-400">
                          <p className="font-medium mb-1">Some children cannot be enrolled this term:</p>
                          <ul className="list-disc list-inside">
                            {ineligibleChildren.map(c => (
                              <li key={c.index}>
                                {c.child.given_name || `Child ${c.index + 1}`} - No age-appropriate sessions available
                              </li>
                            ))}
                          </ul>
                          <p className="mt-2 text-orange-400">
                            You can still proceed with registration for other children.
                          </p>
                        </div>
                      </div>
                    </Card>
                  )}
                  
                  {/* Calculate sibling discount for real-time price display (per-child rank) */}
                  {(() => {
                    // Ordered list of enrolled children (those with sessions).
                    const enrolledOrder = children
                      .map((c, i) => i)
                      .filter((i) => (childSessionSelections[i] || []).length > 0);
                    const rankOf = (childIndex) => enrolledOrder.indexOf(childIndex) + 1; // 1-based, 0 if not enrolled

                    const siblingDiscountsEnabled = rateTable?.sibling_discounts_enabled !== false;
                    const siblingTiersAsc = (siblingDiscountsEnabled && rateTable?.sibling_discounts)
                      ? [...rateTable.sibling_discounts].sort((a, b) => a.child_number - b.child_number)
                      : [];
                    const siblingPctForPosition = (enrollPosition) => {
                      if (enrollPosition < 2 || siblingTiersAsc.length === 0) return 0;
                      let pct = 0;
                      for (const t of siblingTiersAsc) {
                        if (enrollPosition >= t.child_number && t.type === "percentage") pct = t.value;
                      }
                      return pct;
                    };
                    
                    // Volume pricing info
                    const pricingMode = rateTable?.pricing_mode || "progressive";
                    const volumeTiers = rateTable?.volume_pricing_tiers || [];
                    
                    // Helper to get effective price for a session at a given sibling %
                    const getEffectivePrice = (sessionPrice, sessionCountForChild, siblingPct) => {
                      let effectivePrice = sessionPrice;
                      
                      if (pricingMode === "volume" && volumeTiers.length > 0) {
                        // Find applicable volume tier
                        const sortedTiers = [...volumeTiers].sort((a, b) => b.min_sessions - a.min_sessions);
                        const applicableTier = sortedTiers.find(t => sessionCountForChild >= t.min_sessions);
                        if (applicableTier) {
                          effectivePrice = applicableTier.price_per_session;
                        }
                      }
                      
                      // Apply sibling discount (per-child rank)
                      if (siblingPct > 0) {
                        effectivePrice = effectivePrice * (1 - siblingPct / 100);
                      }
                      
                      return Math.round(effectivePrice * 100) / 100; // Round to 2 decimals
                    };
                    
                    return children.map((child, childIndex) => {
                    const availableSessions = getSessionsForChild(childIndex);
                    const selectedForChild = childSessionSelections[childIndex] || [];
                    const childAge = calculateAge(child.birthdate);
                    const isIneligible = availableSessions.length === 0;
                    const willBeEnrolled = selectedForChild.length > 0;
                    // Per-child sibling % by enrolment rank (child #1 → 0%).
                    const childSiblingPct = siblingPctForPosition(rankOf(childIndex));
                    
                    // Calculate term commitment status for this child (with pro-rata)
                    let termWeeks = selectedTerm?.duration_unit === "weeks" 
                      ? (selectedTerm?.duration_value || 10)
                      : Math.ceil((selectedTerm?.duration_value || 70) / 7);
                    
                    // Apply pro-rata
                    if (selectedTerm?.start_date) {
                      const termStart = new Date(selectedTerm.start_date);
                      const today = new Date();
                      const daysSinceStart = Math.floor((today - termStart) / (1000 * 60 * 60 * 24));
                      if (daysSinceStart > 0) {
                        const weeksElapsed = Math.floor(daysSinceStart / 7);
                        termWeeks = Math.max(0, termWeeks - weeksElapsed);
                      }
                    }
                    
                    const totalSessionsForChild = selectedForChild.length * termWeeks;
                    const minRequired = termWeeks; // 1 session/week × term weeks
                    const meetsMinimum = selectedForChild.length === 0 || selectedForChild.length >= 1;
                    
                    return (
                      <Card key={childIndex} className={`p-4 ${isIneligible ? 'bg-[#0a0f1a] opacity-75' : !meetsMinimum ? 'border-red-500/50 bg-red-500/100/5' : willBeEnrolled ? 'border-emerald-500/30 bg-emerald-500/10/30' : ''}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div>
                              <h4 className="font-medium">
                                {child.given_name || `Child ${childIndex + 1}`} {child.last_name}
                              </h4>
                              {childAge !== null && (
                                <p className="text-sm text-slate-400">Age: {childAge} years</p>
                              )}
                            </div>
                            {willBeEnrolled && meetsMinimum && (
                              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full">
                                ✓ Will be enrolled
                              </span>
                            )}
                            {willBeEnrolled && !meetsMinimum && (
                              <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full">
                                ⚠ Need more sessions
                              </span>
                            )}
                            {isIneligible && (
                              <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded-full">
                                No eligible sessions
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <span className={`text-sm ${meetsMinimum ? (willBeEnrolled ? 'text-emerald-400 font-medium' : 'text-slate-400') : 'text-red-400 font-medium'}`}>
                              {selectedForChild.length} session(s)/week
                            </span>
                            {willBeEnrolled && (
                              <p className={`text-xs ${meetsMinimum ? 'text-slate-400' : 'text-red-400'}`}>
                                = {totalSessionsForChild} total ({meetsMinimum ? '✓ meets minimum' : `need ${minRequired - totalSessionsForChild} more`})
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {isIneligible ? (
                          <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                            <p className="text-sm text-orange-400">
                              <AlertCircle className="w-4 h-4 inline mr-1" />
                              No sessions available for the selected type. Please try a different filter or contact admin for assistance.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {(() => {
                              // Universal weekly-days cap surfaces a banner + disables further cards
                              const weekCap = parseInt(rateTable?.max_sessions_per_week_cap) || 0;
                              if (weekCap > 0) {
                                const distinctDays = new Set(
                                  selectedForChild
                                    .map(id => sessions.find(s => s.id === id)?.day_of_week)
                                    .filter(d => d !== undefined && d !== null)
                                );
                                const capReached = distinctDays.size >= weekCap;
                                return (
                                  <div
                                    className={`text-[11px] px-2 py-1 rounded ${
                                      capReached
                                        ? "bg-orange-500/15 text-orange-300 border border-orange-500/40"
                                        : "bg-slate-800/50 text-slate-400 border border-slate-700"
                                    }`}
                                    data-testid={`week-cap-banner-${childIndex}`}
                                  >
                                    {capReached ? "✋" : "ℹ️"}
                                    {" "}
                                    <span className="font-medium">{distinctDays.size} of {weekCap}</span> day(s)/week selected
                                    {capReached ? " — deselect one to switch." : " — pick up to " + weekCap + "."}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {availableSessions.map((session) => {
                              const isSessionSelected = selectedForChild.includes(session.id);
                              
                              // Calculate effective price with volume + sibling discounts
                              const childSessionCount = selectedForChild.length || 1; // Use at least 1 for preview
                              const effectivePrice = getEffectivePrice(session.price, childSessionCount, childSiblingPct);
                              const sessionTotal = isSessionSelected ? (effectivePrice * termWeeks) : 0;
                              const hasDiscount = effectivePrice < session.price;
                              
                              // Session type config
                              const sessionTypeConfig = {
                                social: { label: '🎯 Social', bg: 'bg-green-500/20 text-green-400' },
                                competitive: { label: '⚽ Competitive', bg: 'bg-orange-500/20 text-orange-400' },
                                training: { label: '🏃 Training', bg: 'bg-cyan-500/20 text-cyan-400' },
                                game: { label: '🏆 Game', bg: 'bg-yellow-500/20 text-yellow-400' },
                                tournament: { label: '🥇 Tournament', bg: 'bg-red-500/20 text-red-400' }
                              };
                              const typeInfo = sessionTypeConfig[session?.session_type] || sessionTypeConfig.training;
                              
                              return (
                                <div 
                                  key={session.id} 
                                  className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition ${
                                    isSessionSelected 
                                      ? "bg-blue-500/10 border-blue-500/30" 
                                      : "hover:bg-[#0a0f1a]"
                                  }`}
                                  onClick={() => toggleSessionForChild(childIndex, session.id)}
                                >
                                  <Checkbox
                                    checked={isSessionSelected}
                                    onCheckedChange={() => toggleSessionForChild(childIndex, session.id)}
                                    className="mt-1"
                                  />
                                  <div className="flex-1">
                                    {/* Session Type Badge */}
                                    <span className={`inline-block text-xs px-2 py-0.5 rounded font-medium mb-1 ${typeInfo.bg}`}>
                                      {typeInfo.label}
                                    </span>
                                    
                                    <div className="font-medium">
                                      {getDayName(session.day_of_week)} {session.start_time} - {session.end_time}
                                    </div>
                                    <div className="text-sm text-slate-400">
                                      {session.location || "Main Field"} • 
                                      {hasDiscount ? (
                                        <>
                                          <span className="line-through text-slate-500">${session.price.toFixed(2)}</span>
                                          <span className="ml-1 text-emerald-400 font-medium">${effectivePrice.toFixed(2)}</span>
                                        </>
                                      ) : (
                                        <span>${session.price.toFixed(2)}</span>
                                      )}
                                      /session
                                      {hasDiscount && childSiblingPct > 0 && (
                                        <span className="ml-1 text-xs text-emerald-400">
                                          ({childSiblingPct}% sibling discount)
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* Show total when selected */}
                                    {isSessionSelected && (
                                      <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded text-sm">
                                        <span className="text-emerald-400 font-medium">
                                          ✓ 1 session/week × {termWeeks} weeks = ${sessionTotal.toFixed(2)} for the term
                                        </span>
                                      </div>
                                    )}
                                    
                                    {/* Session description/remarks */}
                                    {(session.description || session.remarks || session.notes) && (
                                      <p className="text-xs text-slate-400 mt-1 italic">
                                        {session.description || session.remarks || session.notes}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </Card>
                    );
                  });
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Fee Summary + Consents + Payment (was Steps 4+5) */}
          {step === 3 && feeCalculation && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">Fee Summary</h2>

              {/* Warning if some children not enrolled */}
              {feeCalculation.total_children > feeCalculation.children_count && (
                <Card className="p-4 bg-orange-500/10 border-orange-500/30">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <div className="text-sm text-orange-400">
                      <p className="font-medium">
                        {feeCalculation.children_count} of {feeCalculation.total_children} children will be enrolled
                      </p>
                      <p className="text-orange-400">
                        Children without selected sessions are not included in this registration.
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Per-Child Breakdown */}
              <Card className="p-4 bg-blue-500/10 border-blue-500/30">
                <h3 className="font-semibold mb-3 text-blue-300">📋 Per-Child Breakdown</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm">
                  <div>
                    <p className="text-slate-400">Term</p>
                    <p className="font-medium">{selectedTerm?.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Duration</p>
                    <p className="font-medium">
                      {feeCalculation.term_weeks} weeks
                      {feeCalculation.is_prorated && (
                        <span className="text-amber-400 ml-2">
                          (Pro-rated from {feeCalculation.full_term_weeks} weeks)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                
                {/* Pro-rata Notice */}
                {feeCalculation.is_prorated && (
                  <div className={`border rounded-lg p-3 mb-4 ${
                    feeCalculation.volume_discount_disabled 
                      ? 'bg-amber-500/20 border-amber-500/30' 
                      : 'bg-blue-500/20 border-blue-500/30'
                  }`}>
                    <p className={`text-sm font-medium ${
                      feeCalculation.volume_discount_disabled ? 'text-amber-300' : 'text-blue-300'
                    }`}>
                      {feeCalculation.volume_discount_disabled 
                        ? '⏰ Late Registration (≥70% of term elapsed)'
                        : '📅 Mid-Term Registration'}
                    </p>
                    <p className={`text-xs mt-1 ${
                      feeCalculation.volume_discount_disabled ? 'text-amber-200/70' : 'text-blue-200/70'
                    }`}>
                      {feeCalculation.volume_discount_disabled 
                        ? `The term has been running for ${feeCalculation.weeks_elapsed} week(s). Volume discounts do not apply for late registrations, but member and sibling discounts still apply.`
                        : `The term has been running for ${feeCalculation.weeks_elapsed} week(s). You're being charged for the remaining ${feeCalculation.term_weeks} week(s) with all applicable discounts.`
                      }
                    </p>
                  </div>
                )}
                
                {/* Detailed breakdown per child */}
                <div className="space-y-4">
                  {feeCalculation.child_breakdowns?.map((cb, idx) => (
                    <div key={idx} className="bg-slate-900/50 p-3 rounded-lg border">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-white">{cb.name}</p>
                          <p className="text-xs text-slate-400">{cb.sessionCount} session{cb.sessionCount > 1 ? 's' : ''}/week × {feeCalculation.term_weeks} weeks</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-blue-400">${cb.finalFee.toFixed(2)}</p>
                          {cb.memberDiscount > 0 && (
                            <p className="text-xs text-emerald-400">-${cb.memberDiscount.toFixed(2)} member discount</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Session details */}
                      <div className="text-xs space-y-1 border-t pt-2 mt-2">
                        {cb.sessions.map((sess, sIdx) => (
                          <div key={sIdx} className="flex justify-between text-slate-400">
                            <span>{getDayName(sess.day)} {sess.time}</span>
                            <span>
                              {sess.originalPrice !== sess.finalPrice ? (
                                <>
                                  <span className="line-through text-slate-400">${sess.originalPrice.toFixed(2)}</span>
                                  <span className="ml-1 text-emerald-400">${sess.finalPrice.toFixed(2)}</span>
                                </>
                              ) : (
                                <span>${sess.finalPrice.toFixed(2)}</span>
                              )}
                              /session
                            </span>
                          </div>
                        ))}
                        <div className="flex justify-between pt-1 border-t font-medium text-slate-300">
                          <span>{cb.isMaxCapacity ? "Weekly total (Fixed Term)" : "Weekly total"}</span>
                          <span>
                            {cb.isMaxCapacity
                              ? <>${(cb.billedSessionsPerWeek ? (cb.weeklyFee / cb.billedSessionsPerWeek) : cb.pricePerSession).toFixed(2)}/session × {cb.billedSessionsPerWeek} sessions/wk × {feeCalculation.term_weeks} wks</>
                              : <>${cb.weeklyFee.toFixed(2)}/week × {feeCalculation.term_weeks} weeks</>}
                          </span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>Total for term</span>
                          <span>${cb.termFee.toFixed(2)}</span>
                        </div>
                        {cb.memberDiscount > 0 && (
                          <div className="flex justify-between text-emerald-400">
                            <span>Member discount ({cb.memberDiscountPercent}%)</span>
                            <span>-${cb.memberDiscount.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Total sessions */}
                <div className="mt-3 pt-3 border-t border-blue-500/30 flex justify-between text-sm font-medium">
                  <span>Total Sessions per Week</span>
                  <span>{Object.values(childSessionSelections).reduce((sum, arr) => sum + arr.length, 0)} session(s)</span>
                </div>
              </Card>

              {/* Fee Breakdown */}
              <Card className="p-4 bg-[#0a0f1a]">
                <h3 className="font-semibold mb-3">💰 Fee Breakdown</h3>
                <div className="space-y-2 text-sm">
                  {/* Original Price (before discounts) */}
                  {feeCalculation.original_fee > feeCalculation.base_fee + (feeCalculation.member_discount_total || 0) && (
                    <div className="flex justify-between text-slate-400">
                      <span>Original Price (before volume discount)</span>
                      <span className="line-through">${feeCalculation.original_fee.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Session Fees after volume pricing */}
                  <div className="flex justify-between">
                    <span>Session Fees ({feeCalculation.children_count} child{feeCalculation.children_count > 1 ? 'ren' : ''} × {feeCalculation.term_weeks} weeks)</span>
                    <span>${(feeCalculation.base_fee + (feeCalculation.member_discount_total || 0) + (feeCalculation.cancellation_offset_total || 0)).toFixed(2)}</span>
                  </div>

                  {/* Cancelled session(s) — not billed */}
                  {feeCalculation.cancellation_offset_total > 0 && (
                    <div className="flex justify-between text-emerald-400" data-testid="fee-cancelled-session-offset">
                      <span>
                        🗓️ Cancelled session not billed
                        {feeCalculation.cancellation_notes?.length > 0
                          ? ` (${feeCalculation.cancellation_notes.join(", ")})`
                          : ""}
                      </span>
                      <span>-${feeCalculation.cancellation_offset_total.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Member Discount - Now shows actual amount */}
                  {feeCalculation.member_discount_total > 0 && (
                    <div className="flex justify-between text-emerald-400">
                      <span>🎫 Member Discount ({feeCalculation.member_discount_source === 'section'
                        ? 'Football Section'
                        : feeCalculation.member_discount_source === 'digital'
                          ? 'Digital Member'
                          : 'Club'} - {feeCalculation.member_discount_percent}%)</span>
                      <span>-${feeCalculation.member_discount_total.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Digital Membership Discount (the highest-membership discount) */}
                  {includeDM && feeCalculation.savings_this_term > 0 && (
                    <div className="flex justify-between text-emerald-400 font-medium" data-testid="fee-dm-discount">
                      <span>💳 Digital Member Discount ({feeCalculation.dm_discount_percent}%)</span>
                      <span>-${feeCalculation.savings_this_term.toFixed(2)}</span>
                    </div>
                  )}

                  {/* Subtotal after all discounts (member + digital membership) */}
                  <div className="flex justify-between font-medium pt-2 border-t" data-testid="fee-subtotal-after-discounts">
                    <span>Subtotal after discounts</span>
                    <span>${(includeDM && feeCalculation.savings_this_term > 0
                      ? feeCalculation.base_fee - feeCalculation.savings_this_term
                      : feeCalculation.base_fee).toFixed(2)}</span>
                  </div>
                  
                  {/* Sibling Discount - Already included in session fees */}
                  {feeCalculation.sibling_discount > 0 ? (
                    <>
                      {/* Hybrid-rule banner: triggered when sibling tier kicked in
                          because of children already enrolled this term, not because
                          this submission has multiple kids. Friendly + specific. */}
                      {feeCalculation.sibling_prior_count_used > 0 && (
                        <div
                          className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 flex items-start gap-2"
                          data-testid="sibling-prior-banner"
                        >
                          <span className="text-emerald-300 text-lg leading-none">🎉</span>
                          <div className="text-sm leading-snug">
                            <p className="text-emerald-200 font-medium">
                              You're saving ${feeCalculation.sibling_discount.toFixed(2)} on this registration!
                            </p>
                            <p className="text-emerald-300/80 text-xs mt-1">
                              {feeCalculation.sibling_prior_names?.length > 0 ? (
                                <>
                                  Because {feeCalculation.sibling_prior_names.slice(0, 3).join(", ")}
                                  {feeCalculation.sibling_prior_names.length > 3 ? ` and ${feeCalculation.sibling_prior_names.length - 3} more` : ""}
                                  {" "}{feeCalculation.sibling_prior_names.length === 1 ? "is" : "are"} already enrolled this term, the family sibling rate applies.
                                </>
                              ) : (
                                <>Your family already has {feeCalculation.sibling_prior_count_used} other {feeCalculation.sibling_prior_count_used === 1 ? "child" : "children"} enrolled this term — the family sibling rate applies.</>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between text-emerald-400 font-medium" data-testid="sibling-discount-applied">
                        <span>
                          👨‍👩‍👧‍👦 Sibling Discount ({feeCalculation.sibling_discount_percent}%)
                          {feeCalculation.sibling_prior_count_used > 0 && (
                            <span className="text-xs text-emerald-300/80 ml-1">
                              · includes {feeCalculation.sibling_prior_count_used} sibling{feeCalculation.sibling_prior_count_used > 1 ? "s" : ""} already enrolled
                            </span>
                          )}
                        </span>
                        <span>Savings of ${feeCalculation.sibling_discount.toFixed(2)} included</span>
                      </div>
                    </>
                  ) : feeCalculation.sibling_count_total > 1 && feeCalculation.sibling_discounts_available && (
                    <div className="flex justify-between text-orange-400 text-xs">
                      <span>Sibling discount not configured in rate table</span>
                      <span></span>
                    </div>
                  )}
                  
                  {/* Discount Code */}
                  {feeCalculation.discount_code_amount > 0 && (
                    <div className="flex justify-between text-amber-400 font-medium">
                      <span>🏷️ Discount Code ({feeCalculation.discount_code})</span>
                      <span>-${feeCalculation.discount_code_amount.toFixed(2)}</span>
                    </div>
                  )}
                  {feeCalculation.discount_code && feeCalculation.discount_code_type === "member_rate" && (
                    <div className="flex justify-between text-amber-400 font-medium">
                      <span>🏷️ {feeCalculation.discount_code}</span>
                      <span>{feeCalculation.discount_code_display}</span>
                    </div>
                  )}
                  
                  {/* Registration Fee with opt-out option. Nothing to decide when
                      every child in the cart has registered before. */}
                  {feeCalculation.registration_fee_enabled
                    && (feeCalculation.new_enrolled_children_count ?? feeCalculation.children_count) === 0 && (
                    <div className="border-2 border-green-500/60 bg-green-500/10 rounded-lg p-4 mt-3"
                      data-testid="registration-fee-already-paid">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-green-300">
                          One-Time Registration Fee
                        </span>
                        <span className="text-green-300 font-semibold">Not charged</span>
                      </div>
                      <p className="text-xs text-green-200/80 mt-1">
                        The one-time fee of $
                        {feeCalculation.registration_fee_per_child?.toFixed(2)} is not being
                        charged for {(feeCalculation.children_count || 1) > 1 ? "these children" : "this child"}.
                      </p>
                    </div>
                  )}
                  {feeCalculation.registration_fee_enabled
                    && (feeCalculation.new_enrolled_children_count ?? feeCalculation.children_count) > 0 && (
                    <div className={`border-2 rounded-lg p-4 mt-3 ${
                      skipRegistrationFee 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-amber-500 bg-amber-500/10'
                    }`}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="registration-fee-checkbox"
                          checked={!skipRegistrationFee}
                          onCheckedChange={(checked) => {
                            if (!checked) {
                              // User is trying to uncheck (skip the fee)
                              const confirmed = window.confirm(
                                "⚠️ EXISTING ACADEMY MEMBER?\n\n" +
                                "The registration fee is ONLY for NEW registrations.\n\n" +
                                "If your child has attended SCC Football Academy before, you can skip this fee.\n\n" +
                                "Click 'OK' if you are a RETURNING member (skip fee)\n" +
                                "Click 'Cancel' if you are NEW (keep fee)"
                              );
                              if (confirmed) {
                                setSkipRegistrationFee(true);
                              }
                            } else {
                              setSkipRegistrationFee(false);
                            }
                          }}
                          className="mt-1 h-5 w-5 border-2 border-slate-400 data-[state=checked]:bg-amber-500/100 data-[state=checked]:border-amber-500"
                        />
                        <div className="flex-1">
                          <label htmlFor="registration-fee-checkbox" className="cursor-pointer">
                            <div className="flex items-center justify-between">
                              <span className={`font-semibold ${skipRegistrationFee ? "text-slate-400 line-through" : "text-amber-400"}`}>
                                One-Time Registration Fee ({feeCalculation.new_enrolled_children_count ?? feeCalculation.children_count} new child × ${feeCalculation.registration_fee_per_child?.toFixed(2)})
                              </span>
                              <span className={`font-bold text-lg ${skipRegistrationFee ? "text-slate-400 line-through" : "text-amber-400"}`}>
                                ${(feeCalculation.registration_fee_per_child * (feeCalculation.new_enrolled_children_count ?? feeCalculation.children_count)).toFixed(2)}
                              </span>
                            </div>
                          </label>
                          
                          {/* Fee-waived note: count = total enrolled children - chargeable children.
                              When the parent unchecks one or more "Charge registration fee" boxes in Step 1,
                              this summary tile shows how many were waived so nothing feels silently skipped. */}
                          {feeCalculation.children_count > feeCalculation.new_enrolled_children_count && (
                            <div className="mt-2 p-2 bg-emerald-500/10 rounded border border-emerald-500/30" data-testid="reg-fee-waived-note">
                              <p className="text-xs text-emerald-400 font-medium">
                                ✓ Registration fee NOT charged for {feeCalculation.children_count - feeCalculation.new_enrolled_children_count} child(ren) (unchecked on their card). Admin will verify at payment approval.
                              </p>
                            </div>
                          )}
                          {!skipRegistrationFee ? (
                            <div className="mt-2 p-2 bg-amber-500/20 rounded border border-amber-500/30">
                              <p className="text-xs text-amber-400 font-medium">
                                This one-time fee is charged once per child, ever.
                              </p>
                              <p className="text-xs text-amber-400 mt-1">
                                It is included here because no earlier term has been paid for{" "}
                                {(feeCalculation?.children_count || 1) > 1 ? "these children" : "this child"}.
                                Paid it before? Untick it on their card in Step 1.
                              </p>
                            </div>
                          ) : (
                            <div className="mt-2 p-2 bg-green-500/20 rounded border border-green-500/30">
                              <p className="text-xs text-green-400 font-medium flex items-center gap-1">
                                ✅ Registration fee waived (returning member)
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Total Discounts Summary */}
                  {(feeCalculation.member_discount_total > 0 || feeCalculation.sibling_discount > 0) && (
                    <div className="flex justify-between text-emerald-400 font-semibold pt-2 border-t bg-emerald-500/10 -mx-4 px-4 py-2 mt-2">
                      <span>Total Savings</span>
                      <span>-${((feeCalculation.member_discount_total || 0) + feeCalculation.sibling_discount + (includeDM ? feeCalculation.savings_this_term : 0)).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </Card>

              {/* Existing Digital Member — auto-applied badge (shown instead of the upsell) */}
              {feeCalculation?.has_active_dm && (
                <Card className="p-4 bg-gradient-to-r from-emerald-500/10 to-green-500/10 border-emerald-500/40" data-testid="active-dm-badge">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-green-300 mb-0.5">
                        {feeCalculation.member_discount_source === 'digital'
                          ? `✅ Digital Member — ${feeCalculation.dm_discount_percent}% discount applied automatically`
                          : `✅ Digital Membership active — no charge this term`}
                      </h3>
                      <p className="text-sm text-emerald-600">
                        Your Digital Membership is active
                        {feeCalculation.active_dm_expires_at
                          ? ` until ${new Date(feeCalculation.active_dm_expires_at).toLocaleDateString()}`
                          : ""}
                        {feeCalculation.member_discount_source === 'digital'
                          ? ". You won't be charged again — the discount is applied to this term's fees."
                          : `. You won't be charged again. Your ${
                              feeCalculation.member_discount_source === 'section' ? 'Football Section' : 'Club'
                            } membership discount of ${feeCalculation.member_discount_percent}% has been applied to this term instead, as it is the better rate.`}
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Digital Membership Upsell - Only show if enabled AND user is NOT an
                  SCC member (any tier) AND doesn't already have DM. SCC members
                  already receive the 10% member discount so DM would double-dip. */}
              {feeCalculation?.dm_enabled
                && !feeCalculation?.has_active_dm
                && !isSccMember
                && !loggedInUser?.is_scc_member
                && loggedInUser?.member_type !== 'section'
                && linkedMemberType !== 'section'
                && linkedMemberType !== 'club_member' && (
              <Card className="p-4 bg-gradient-to-r from-emerald-500/10 to-green-500/10 border-green-500/30">
                <div className="flex items-start gap-3 mb-4">
                  <TrendingDown className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-green-300 mb-1">
                      💰 Save More with Digital Membership!
                    </h3>
                    <p className="text-sm text-emerald-600 font-medium">
                      Get {feeCalculation.dm_discount_percent}% off all academy fees for 1 full year
                    </p>
                  </div>
                </div>

                {/* Member Benefits */}
                <div className="bg-white/80 p-3 rounded-lg mb-4 border border-green-500/30">
                  <p className="text-sm font-semibold text-green-400 mb-2">🎁 Member Benefits Include:</p>
                  <ul className="text-sm text-slate-700 space-y-1.5">
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span><strong>{feeCalculation.dm_discount_percent}% off</strong> all academy session fees</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span>Discounts on <strong>merchandise</strong></span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span>Discounted rates for <strong>camps & academy events</strong></span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span>Reduced rates for <strong>private 1-on-1 coaching</strong></span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span>Discounted <strong>tournament entry fees</strong></span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span>Partner discounts on <strong>equipment & gear</strong></span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span>Additional <strong>sibling discount stacking</strong></span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span>Access to <strong>member-only clinics & events</strong></span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/80 p-4 rounded-lg mb-4 border border-green-500/30">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-600">Digital Membership</p>
                      <p className="text-xl font-bold text-slate-900">
                        ${feeCalculation.dm_price.toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-400">(1 full year)</p>
                    </div>
                    <div>
                      <p className="text-slate-600">{includeDM ? "Savings This Term" : "You Could Save This Term"}</p>
                      <p className="text-xl font-bold text-emerald-600">
                        ${feeCalculation.potential_savings_this_term.toFixed(2)}
                      </p>
                      {!includeDM && (
                        <p className="text-xs text-orange-600 font-medium">Select to apply discount!</p>
                      )}
                    </div>
                  </div>
                  {feeCalculation.net_benefit > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-500/30 text-center">
                      <p className="text-sm text-slate-600">If you stay 4 terms (1 year):</p>
                      <p className="text-xs text-slate-400 mb-1">
                        (${feeCalculation.potential_savings_this_term.toFixed(2)} × 4 terms) - ${feeCalculation.dm_price.toFixed(2)} membership =
                      </p>
                      <p className="text-xl font-bold text-emerald-600">
                        Net Savings: ${feeCalculation.net_benefit.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 p-3 bg-white/80 rounded border border-green-500/30">
                  <Checkbox
                    checked={includeDM}
                    onCheckedChange={setIncludeDM}
                    id="include-dm"
                  />
                  <label htmlFor="include-dm" className="flex-1 cursor-pointer">
                    <span className="font-medium text-slate-900">Add Digital Membership</span>
                    <span className="text-sm text-slate-600 ml-2">(${feeCalculation.dm_price.toFixed(2)}/year)</span>
                  </label>
                </div>
                
                {/* Important note for existing Club Members (not Section Members - they don't see this) */}
                {(linkedMemberType === 'scc_member' || (isSccMember && linkedMemberType !== 'section')) && (
                  <div className="mt-3 p-3 bg-amber-500/20 border border-amber-500/30 rounded-lg text-sm">
                    <p className="text-amber-400 font-medium mb-1">⚠️ Note for Club Members:</p>
                    <p className="text-amber-400 text-xs">
                      If your Club Member discount is already equal to or higher than the Digital Membership discount ({feeCalculation.dm_discount_percent}%), 
                      <strong> no additional discount will apply to term fees</strong>. 
                    </p>
                    <p className="text-amber-400 text-xs mt-2">
                      Digital Membership provides: Section-level benefits, merchandise discounts, merchant partner discounts, 
                      camps, private coaching rates, and official Football Section membership.
                    </p>
                    <p className="text-amber-400 text-xs mt-2 font-medium">
                      If you do not wish to subscribe, please unselect Digital Membership before proceeding.
                    </p>
                  </div>
                )}
              </Card>
              )}

              {/* Terms & Conditions Consent Section */}
              <Card className="p-4 bg-amber-500/10 border-amber-500/30">
                <h3 className="font-semibold mb-4 text-amber-300">📋 Terms & Conditions</h3>
                <p className="text-sm text-amber-400 mb-4">Please read and agree to the following before proceeding with payment:</p>
                
                {/* Dynamic Consent Forms from Database */}
                {consentTexts.filter(t => t.is_active).sort((a, b) => a.order - b.order).map((consent) => {
                  const consentState = consent.type === 'pdpa' ? consentPDPA : 
                                       consent.type === 'safe_sport' ? consentSafeSport : consentIndemnity;
                  const setConsentState = consent.type === 'pdpa' ? setConsentPDPA : 
                                          consent.type === 'safe_sport' ? setConsentSafeSport : setConsentIndemnity;
                  
                  return (
                    <div key={consent.id} className="mb-4 p-4 bg-white rounded-lg border border-amber-500/30">
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id={`consent-${consent.id}`}
                          checked={consentState}
                          onCheckedChange={setConsentState}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <label htmlFor={`consent-${consent.id}`} className="font-medium text-slate-900 cursor-pointer">
                            {consent.title}
                          </label>
                          <div className="mt-2 text-sm text-slate-700 bg-slate-50 p-3 rounded max-h-48 overflow-y-auto border border-slate-200">
                            {consent.type === 'safe_sport' ? (
                              <div className="whitespace-pre-wrap">
                                {consent.content.split('safesport.sg').map((part, i, arr) => (
                                  <span key={i}>
                                    {part}
                                    {i < arr.length - 1 && (
                                      <a href="https://safesport.sg" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">safesport.sg</a>
                                    )}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <ContentWithImages content={consent.content} />
                            )}
                          </div>
                          <p className={`text-xs mt-2 ${consentState ? 'text-green-400' : 'text-amber-400'}`}>
                            {consentState ? "✓ Agreed" : "⚠️ Please check the box to agree"}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Fallback if consent texts not loaded */}
                {consentTexts.length === 0 && (
                  <>
                    {/* 1. PDPA Consent */}
                    <div className="mb-4 p-4 bg-white rounded-lg border border-amber-500/30">
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="consent-pdpa"
                          checked={consentPDPA}
                          onCheckedChange={setConsentPDPA}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <label htmlFor="consent-pdpa" className="font-medium text-slate-900 cursor-pointer">
                            Personal Data Protection Act (PDPA) Consent
                          </label>
                          <div className="mt-2 text-sm text-slate-700 bg-slate-50 p-3 rounded max-h-32 overflow-y-auto border border-slate-200">
                            <p>As the parent/guardian of the child/children participating in the Programme, I hereby agree to be bound by the terms and conditions herein and grant the <strong>Singapore Cricket Club</strong> and/or its partners/agents running the football program the moral rights waiver to use, amend, modify, adapt, reproduce, publish any still or moving images of the participants and/or their parents or legal guardians that are taken as part of the training/games for any promotional or marketing or any other purposes as the Club may in its absolute discretion deem fit.</p>
                          </div>
                          <p className={`text-xs mt-2 ${consentPDPA ? 'text-green-400' : 'text-amber-400'}`}>
                            {consentPDPA ? "✓ Agreed" : "⚠️ Please check the box to agree"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 2. Safe Sport Acknowledgment */}
                    <div className="mb-4 p-4 bg-white rounded-lg border border-amber-500/30">
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="consent-safesport"
                          checked={consentSafeSport}
                          onCheckedChange={setConsentSafeSport}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <label htmlFor="consent-safesport" className="font-medium text-slate-900 cursor-pointer">
                            Safe Sport Acknowledgment
                          </label>
                          <div className="mt-2 text-sm text-slate-700 bg-slate-50 p-3 rounded max-h-32 overflow-y-auto border border-slate-200">
                            <p className="mb-2">As part of our commitment to providing a safe and positive environment for all our players, coaches and managers, we follow the Safe Sport guidelines as outlined by SportSG and available for perusal on <a href="https://safesport.sg" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">safesport.sg</a> website.</p>
                            <p>By checking this box, I acknowledge that I have read and understand the Safe Sport policies and agree to uphold the principles of respect, safety, and protection of all coaches, managers, players, especially minors, from abuse and misconduct.</p>
                          </div>
                          <p className={`text-xs mt-2 ${consentSafeSport ? 'text-green-400' : 'text-amber-400'}`}>
                            {consentSafeSport ? "✓ Agreed" : "⚠️ Please check the box to agree"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 3. Indemnity and Waiver */}
                    <div className="mb-2 p-4 bg-white rounded-lg border border-amber-500/30">
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="consent-indemnity"
                          checked={consentIndemnity}
                          onCheckedChange={setConsentIndemnity}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <label htmlFor="consent-indemnity" className="font-medium text-slate-900 cursor-pointer">
                            Indemnity and Waiver
                          </label>
                          <div className="mt-2 text-sm text-slate-700 bg-slate-50 p-3 rounded max-h-48 overflow-y-auto border border-slate-200">
                            <p>As the parent/guardian, I acknowledge and agree to the terms and conditions including indemnity and liability waiver for the Singapore Cricket Club and Football and More Limited.</p>
                          </div>
                          <p className={`text-xs mt-2 ${consentIndemnity ? 'text-green-400' : 'text-amber-400'}`}>
                            {consentIndemnity ? "✓ Agreed" : "⚠️ Please check the box to agree"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {!allConsentsGiven && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 font-medium">
                      ⚠️ Please agree to all terms and conditions above to proceed with payment.
                    </p>
                  </div>
                )}
              </Card>

              {/* Discount Code */}
              <Card className="p-4 bg-amber-500/100/5 border-amber-500/30">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-amber-400" />
                  Have a Discount Code?
                </h3>
                
                {validatedDiscount ? (
                  <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <div>
                        <p className="font-medium text-white">{validatedDiscount.code}</p>
                        <p className="text-sm text-emerald-400">{validatedDiscount.display}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeDiscountCode}
                      className="text-red-400 hover:text-red-300"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Enter discount code"
                        value={discountCode}
                        onChange={(e) => {
                          setDiscountCode(e.target.value.toUpperCase());
                          setDiscountError("");
                        }}
                        className="bg-slate-900/50 border-slate-700 font-mono"
                      />
                      {discountError && (
                        <p className="text-sm text-red-400 mt-1">{discountError}</p>
                      )}
                    </div>
                    <Button
                      onClick={validateDiscountCode}
                      disabled={discountLoading || !discountCode.trim()}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {discountLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        "Apply"
                      )}
                    </Button>
                  </div>
                )}
                
                {validatedDiscount?.discount_type === "member_rate" && !isSccMember && (
                  <p className="text-xs text-emerald-400 mt-2">
                    ✓ You&apos;ll receive SCC member rates with this code!
                  </p>
                )}
              </Card>

              {/* Kit selection */}
              {selectedTerm?.id && kitChildrenList.length > 0 && (
                <Card className="p-4" data-testid="wizard-kit-card">
                  <KitSelectionStep
                    termId={selectedTerm.id}
                    childrenList={kitChildrenList}
                    onChange={(sel, total) => { setKitSelections(sel); setKitExtrasTotal(total); }}
                  />
                </Card>
              )}

              {/* Payment Method Selection */}
              <Card className={`p-4 ${!allConsentsGiven ? 'opacity-50 pointer-events-none' : ''}`}>
                <h3 className="font-semibold mb-4">💳 Select Payment Method</h3>
                {!allConsentsGiven && (
                  <p className="text-sm text-amber-600 mb-4">Please agree to all terms above to unlock payment options.</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* PayNow Option */}
                  <div
                    onClick={() => setPaymentMethod("paynow")}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      paymentMethod === "paynow"
                        ? "border-green-500 bg-emerald-500/10"
                        : "border-white/5 hover:border-slate-700"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="radio"
                        checked={paymentMethod === "paynow"}
                        onChange={() => setPaymentMethod("paynow")}
                        className="w-4 h-4 text-emerald-400"
                      />
                      <span className="font-semibold text-lg">PayNow</span>
                      <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full">
                        No Fees!
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 ml-7">
                      Pay via your banking app
                    </p>
                  </div>

                  {/* Card Option */}
                  <div
                    onClick={() => setPaymentMethod("card")}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      paymentMethod === "card"
                        ? "border-blue-500 bg-blue-500/10"
                        : "border-white/5 hover:border-slate-700"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="radio"
                        checked={paymentMethod === "card"}
                        onChange={() => setPaymentMethod("card")}
                        className="w-4 h-4 text-blue-400"
                      />
                      <span className="font-semibold text-lg">Credit/Debit Card</span>
                    </div>
                    <p className="text-sm text-slate-400 ml-7">
                      Visa, Mastercard, AMEX
                    </p>
                    <p className="text-xs text-orange-400 ml-7 mt-1">
                      Card processing fee applies
                    </p>
                  </div>
                </div>
              </Card>

              {/* Total */}
              <Card className="p-6 bg-blue-500/10 border-blue-500/30">
                <div className="space-y-3">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Session Fees ({feeCalculation.children_count} child(ren))</span>
                      <span>${feeCalculation.base_fee.toFixed(2)}</span>
                    </div>
                    {feeCalculation.sibling_discount > 0 && (
                      <div className="flex justify-between text-emerald-400 text-sm">
                        <span>👨‍👩‍👧‍👦 Sibling Discount ({feeCalculation.sibling_discount_percent}%)</span>
                        <span>Savings of ${feeCalculation.sibling_discount.toFixed(2)} included</span>
                      </div>
                    )}
                    {includeDM && (
                      <>
                        <div className="flex justify-between text-emerald-400">
                          <span>Digital Member Discount ({feeCalculation.dm_discount_percent}%)</span>
                          <span>-${feeCalculation.savings_this_term.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Digital Membership</span>
                          <span>${feeCalculation.dm_price.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    {feeCalculation.cancellation_offset_total > 0 && (
                      <div className="flex justify-between text-emerald-400 text-sm" data-testid="summary-cancelled-session-offset">
                        <span>
                          🗓️ Cancelled session not billed
                          {feeCalculation.cancellation_notes?.length > 0
                            ? ` (${feeCalculation.cancellation_notes.join(", ")})`
                            : ""}
                        </span>
                        <span>-${feeCalculation.cancellation_offset_total.toFixed(2)} included</span>
                      </div>
                    )}
                    {feeCalculation.registration_fee > 0 && (
                      <div className="flex justify-between" data-testid="summary-registration-fee">
                        <span>One-Time Registration Fee</span>
                        <span>${feeCalculation.registration_fee.toFixed(2)}</span>
                      </div>
                    )}
                    {feeCalculation.optional_items_total > 0 && (
                      <div className="flex justify-between" data-testid="summary-optional-items">
                        <span>Optional Items</span>
                        <span>${feeCalculation.optional_items_total.toFixed(2)}</span>
                      </div>
                    )}
                    {paymentMethod === "card" && (
                      <div className="flex justify-between text-orange-400" data-testid="fee-card-processing">
                        <span>Card processing fee</span>
                        <span>+${(includeDM ? feeCalculation.card_fee_with_dm : feeCalculation.card_fee_without_dm).toFixed(2)}</span>
                      </div>
                    )}
                    {(includeDM ? feeCalculation.credit_applied_with_dm : feeCalculation.credit_applied) > 0 && (
                      <div className="flex justify-between text-emerald-300 font-medium" data-testid="summary-cancellation-credit">
                        <span>
                          🎟️ Cancelled-session credit applied
                          {feeCalculation.credit_notes?.length > 0
                            ? ` (${feeCalculation.credit_notes.join(", ")})`
                            : ""}
                        </span>
                        <span>-${(includeDM ? feeCalculation.credit_applied_with_dm : feeCalculation.credit_applied).toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-2xl font-bold text-white">TOTAL</h3>
                        {paymentMethod === "paynow" && (
                          <p className="text-sm text-emerald-400 font-medium">✓ No processing fees!</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-blue-400" data-testid="fee-grand-total">
                          ${((paymentMethod === "card"
                            ? (includeDM ? feeCalculation.total_card_with_dm : feeCalculation.total_card_without_dm)
                            : (includeDM ? feeCalculation.total_with_dm : feeCalculation.total_without_dm)
                          ) + (kitExtrasTotal || 0)).toFixed(2)}
                        </p>
                        {kitExtrasTotal > 0 && (
                          <p className="text-xs text-fuchsia-300" data-testid="fee-total-kit-note">includes ${kitExtrasTotal.toFixed(2)} kit add-ons</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 3 && (
            <div className="space-y-4 mt-8 pt-6 border-t">
              {/* Secondary actions row */}
              {step === 1 && (
                <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
                  {/* Save Draft Button */}
                  {!isExistingMember && (
                    <Button variant="outline" onClick={() => saveDraft(true)} disabled={savingDraft || !parentEmail}
                      className="text-slate-400 border-slate-700 text-sm" size="sm" data-testid="save-draft-btn">
                      {savingDraft ? <Clock className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      {savingDraft ? "Saving..." : "Save Draft"}
                    </Button>
                  )}
                  
                  {/* Add Another Child */}
                  <div className="flex items-center gap-2">
                    {children.length >= 1 && rateTable?.sibling_discounts?.length > 0 && (
                      <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                        Sibling discounts apply!
                      </span>
                    )}
                    <Button type="button" variant="outline" size="sm" onClick={addChild}
                      className="text-blue-400 border-blue-500/50 hover:bg-blue-500/10">
                      <Plus className="w-4 h-4 mr-1" />Add Child
                    </Button>
                  </div>
                </div>
              )}

              {/* Save Draft button on step 2 as well (sessions) */}
              {step === 2 && !isExistingMember && parentEmail && (
                <div className="flex justify-center mb-2">
                  <Button variant="outline" onClick={() => saveDraft(true)} disabled={savingDraft}
                    className="text-slate-400 border-slate-700 text-sm" size="sm" data-testid="save-draft-btn-step2">
                    {savingDraft ? <Clock className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    {savingDraft ? "Saving..." : "Save & Finish Later"}
                  </Button>
                </div>
              )}

              <div className="flex justify-between">
                {step > 1 ? (
                  <Button variant="outline" onClick={() => setStep(step - 1)} className="text-slate-400 hover:text-white border-slate-700" data-testid="wizard-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" />Back
                  </Button>
                ) : <div />}
                <Button onClick={nextStep} disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 min-w-[180px]">
                  {step === 2 ? "Review & Pay" : "Next: Select Sessions"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3 Submit Button */}
          {step === 3 && feeCalculation && (
            <div className="space-y-4 mt-8 pt-6 border-t">
              <Button onClick={handleSubmit} disabled={loading || !allConsentsGiven}
                className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg">
                {loading ? "Submitting..." : "Submit Registration & Proceed to Payment"}
              </Button>
              <Button variant="ghost" onClick={() => setStep(2)}
                className="w-full text-slate-400 hover:text-slate-300" disabled={loading}>
                <ArrowLeft className="w-4 h-4 mr-2" />Back to Sessions
              </Button>
              <div className="text-center">
                <Button variant="ghost"
                  onClick={() => {
                    if (isExistingMember) { navigate("/dashboard"); }
                    else if (hasDraft) { toast.info("Progress saved. Resume later using the link in your email."); navigate("/"); }
                    else if (window.confirm("Cancel registration?")) { navigate("/"); }
                  }}
                  className="text-slate-400 hover:text-slate-300">
                  {isExistingMember ? "Back to Dashboard" : "Cancel Registration"}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
